import React, { useEffect, useMemo, useState, useRef } from 'react';
import { flushSync } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Card } from '../../components/ui/Card';
import { RefreshCw, X, Calendar, CalendarDays, BarChart2, ChevronDown, ChevronRight } from 'lucide-react';

export default function CombinedTrafficSources() {
  const [activeSection, setActiveSection] = useState('daily'); // 'daily' | 'monthly'
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('last7');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [selectedPlatform, setSelectedPlatform] = useState('web'); // all | web | ios | android | app
  const [isNonPaidOpen, setIsNonPaidOpen] = useState(true);
  const [isPaidOpen, setIsPaidOpen] = useState(true);
  const [isAdjNonPaidOpen, setIsAdjNonPaidOpen] = useState(true);
  const [isAdjPaidOpen, setIsAdjPaidOpen] = useState(true);
  const [reportEmail, setReportEmail] = useState('');
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [sendReportStatus, setSendReportStatus] = useState(null);

  // Separate GA and Adjust rows; both only care about Revenue metric for UI
  const [gaRows, setGaRows] = useState([]);
  const [adjustRows, setAdjustRows] = useState([]);

  // Daily Google Ads & Meta Ads cost data
  const [dailyGoogleAdsData, setDailyGoogleAdsData] = useState([]);
  const [dailyMetaAdsData, setDailyMetaAdsData] = useState([]);

  // Monthly data states
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [gaMonthlyRows, setGaMonthlyRows] = useState([]);
  const [adjustMonthlyRows, setAdjustMonthlyRows] = useState([]);
  const [selectedMonthlyMetric, setSelectedMonthlyMetric] = useState('revenue');
  const [isMonthlyNonPaidOpen, setIsMonthlyNonPaidOpen] = useState(true);
  const [isMonthlyPaidOpen, setIsMonthlyPaidOpen] = useState(true);
  const [isMonthlyAdjNonPaidOpen, setIsMonthlyAdjNonPaidOpen] = useState(true);
  const [isMonthlyAdjPaidOpen, setIsMonthlyAdjPaidOpen] = useState(true);

  // Monthly Performance Trends (Google + Meta Ads)
  const [monthlyPerformanceData, setMonthlyPerformanceData] = useState(null);
  const [isLoadingMonthlyPerformance, setIsLoadingMonthlyPerformance] = useState(false);
  const [expandedPlatforms, setExpandedPlatforms] = useState([]);

  const formatDateForAPI = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getApiDateRangeParam = (range) => {
    switch (range) {
      case 'today':
      case 'yesterday':
      case 'last7':
      case 'last30':
      case 'last90':
      case 'thisMonth':
      case 'lastMonth':
      case 'all':
      case 'custom':
        return range;
      default:
        return 'last30';
    }
  };

  const getDateRangeForFilter = (filter) => {
    const today = new Date();
    let startDate;
    let endDate;

    switch (filter) {
      case 'today':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 23, 59, 59);
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        break;
      case 'lastMonth':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        break;
      case 'last7':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        endDate = today;
        break;
      case 'last30':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
        break;
      case 'last90':
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 90);
        endDate = today;
        break;
      case 'all':
        startDate = new Date(2020, 0, 1);
        endDate = today;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        } else {
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
          endDate = today;
        }
        break;
      default:
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        endDate = today;
    }

    return { startDate, endDate };
  };

  const selectedRangeLabel = useMemo(() => {
    const r = getDateRangeForFilter(dateRange);
    if (!r || !r.startDate || !r.endDate) return '';
    return `${r.startDate.toLocaleDateString('tr-TR')} - ${r.endDate.toLocaleDateString('tr-TR')}`;
  }, [dateRange, customStartDate, customEndDate]);

  const formatCompactNumber = (value) => {
    const n = Number(value) || 0;
    if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
    return n.toLocaleString('tr-TR');
  };

  const formatCurrency = (value) => {
    const n = Number(value) || 0;
    return `₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
  };

  const metricLabelMap = {
    revenue: 'REVENUE',
    sessions: 'SESSIONS',
    installs: 'INSTALLS',
    conversion: 'CONVERSION RATE'
  };

  const getRowMetricValue = (row, metric) => {
    switch (metric) {
      case 'sessions':
        return Number(row.sessions || 0);
      case 'installs':
        return Number(row.installs || 0);
      case 'conversion':
        return Number(row.conversionRate || 0);
      case 'revenue':
      default:
        return Number(row.revenue || 0);
    }
  };

  const formatMetricAggregate = (value, metric) => {
    if (metric === 'revenue') return formatCurrency(value);
    if (metric === 'conversion') return `${(Number(value || 0) * 100).toFixed(1)}%`;
    return formatCompactNumber(value);
  };

  const formatMetricValue = (row, metric) => {
    if (!row) return '-';
    const value = getRowMetricValue(row, metric);
    if (metric === 'revenue') return formatCurrency(value);
    if (metric === 'conversion') return `${(Number(value || 0) * 100).toFixed(1)}%`;
    return formatCompactNumber(value);
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      setDateRange('custom');
      setIsCustomDateOpen(false);
    }
  };

  const handleCustomDateReset = () => {
    setCustomStartDate(null);
    setCustomEndDate(null);
    setDateRange('last30');
    setIsCustomDateOpen(false);
  };

  const handleSendCombinedReportEmail = async () => {
    try {
      if (!reportEmail) {
        setSendReportStatus({ type: 'error', message: 'Lütfen bir e‑posta adresi gir.' });
        return;
      }

      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setSendReportStatus({
          type: 'error',
          message: 'Brand ID bulunamadı (currentBrandId).'
        });
        return;
      }

      setIsSendingReport(true);
      setSendReportStatus(null);

      // Save current collapse states so we can restore them after snapshot
      const prevStates = {
        isNonPaidOpen,
        isPaidOpen,
        isAdjNonPaidOpen,
        isAdjPaidOpen
      };

      // Force all sections open synchronously so the DOM renders all rows
      flushSync(() => {
        setIsNonPaidOpen(true);
        setIsPaidOpen(true);
        setIsAdjNonPaidOpen(true);
        setIsAdjPaidOpen(true);
      });

      // Wait for the DOM to fully re-render with all rows visible
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      const container = document.getElementById('combined-traffic-email-snapshot');
      if (!container) {
        // Restore states before bailing
        flushSync(() => {
          setIsNonPaidOpen(prevStates.isNonPaidOpen);
          setIsPaidOpen(prevStates.isPaidOpen);
          setIsAdjNonPaidOpen(prevStates.isAdjNonPaidOpen);
          setIsAdjPaidOpen(prevStates.isAdjPaidOpen);
        });
        setSendReportStatus({
          type: 'error',
          message: 'Tablo içeriği bulunamadı.'
        });
        setIsSendingReport(false);
        return;
      }

      // Snapshot HTML of the Combined Summary + GA + Adjust tables
      let tableHtml = container.outerHTML;

      // Restore the original collapse states immediately
      flushSync(() => {
        setIsNonPaidOpen(prevStates.isNonPaidOpen);
        setIsPaidOpen(prevStates.isPaidOpen);
        setIsAdjNonPaidOpen(prevStates.isAdjNonPaidOpen);
        setIsAdjPaidOpen(prevStates.isAdjPaidOpen);
      });

      // === Clean up HTML for email using DOMParser (reliable DOM manipulation) ===
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div id="email-root">${tableHtml}</div>`, 'text/html');
      const root = doc.getElementById('email-root');

      // Remove SVG icons (Lucide React icons like RefreshCw, ChevronDown etc.)
      root.querySelectorAll('svg').forEach(el => el.remove());

      // Remove input elements (search bars)
      root.querySelectorAll('input').forEach(el => el.remove());

      // Remove select elements (platform dropdown)
      root.querySelectorAll('select').forEach(el => el.remove());

      // Remove button elements (refresh buttons etc.)
      root.querySelectorAll('button').forEach(el => el.remove());

      // Convert local image paths to absolute URLs for email
      root.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src') || '';
        if (src === '/GA4logoicon.svg') {
          img.setAttribute('src', 'https://platform.glowytics.com/Google-Analytics-Logo.png');
          img.setAttribute('style', 'height:16px;width:auto;display:inline-block;vertical-align:middle;');
        } else if (src === '/adjustlogopng.png') {
          img.setAttribute('src', 'https://platform.glowytics.com/adjustlogopng.png');
          img.setAttribute('style', 'height:16px;width:auto;display:inline-block;vertical-align:middle;border-radius:50%;');
        } else if (src === '/google_ads-512.webp') {
          img.setAttribute('src', 'https://platform.glowytics.com/google_ads-512.webp');
          img.setAttribute('style', 'height:16px;width:auto;display:inline-block;vertical-align:middle;');
        } else if (src === '/6033716.png') {
          img.setAttribute('src', 'https://platform.glowytics.com/6033716.png');
          img.setAttribute('style', 'height:16px;width:auto;display:inline-block;vertical-align:middle;');
        } else if (src.startsWith('/')) {
          img.setAttribute('src', `https://platform.glowytics.com${src}`);
        }
      });

      // Remove collapse/expand arrows from all text
      root.querySelectorAll('td, span').forEach(el => {
        if (el.childNodes) {
          el.childNodes.forEach(node => {
            if (node.nodeType === 3) { // Text node
              node.textContent = node.textContent.replace(/▼/g, '').replace(/►/g, '');
            }
          });
        }
      });

      // Remove emoji characters but keep text
      const removeEmojis = (node) => {
        if (node.nodeType === 3) {
          node.textContent = node.textContent
            .replace(/💰/g, '')
            .replace(/➖/g, '')
            .replace(/✖️/g, '')
            .replace(/📊/g, '');
        } else if (node.childNodes) {
          node.childNodes.forEach(removeEmojis);
        }
      };
      removeEmojis(root);

      // Remove empty container divs that had only icons/buttons
      root.querySelectorAll('div').forEach(div => {
        if (div.textContent.trim() === '' && !div.querySelector('table, td, tr, th, img, h2, h3')) {
          div.remove();
        }
      });

      // Common border style for all cells
      const cellBorder = 'border:1px solid #d1d5db;';

      // 1) First pass: Apply consistent inline styles to ALL table cells
      //    - First column (index 0): left-aligned (source/channel label)
      //    - All other columns: right-aligned (numeric values)
      root.querySelectorAll('table').forEach(table => {
        // Style header cells
        table.querySelectorAll('thead th').forEach((th, i) => {
          if (i === 0) {
            th.setAttribute('style', `background:#f3f4f6; font-weight:600; font-size:10px; color:#6b7280; text-align:left; padding:8px 10px; ${cellBorder} text-transform:uppercase;`);
          } else {
            th.setAttribute('style', `background:#f3f4f6; font-weight:600; font-size:10px; color:#6b7280; text-align:right; padding:8px 10px; ${cellBorder} text-transform:uppercase; white-space:nowrap;`);
          }
        });

        // Style body cells - default (will be overridden for special rows below)
        table.querySelectorAll('tbody tr').forEach(tr => {
          const cls = tr.getAttribute('class') || '';
          const classes = cls.split(/\s+/);
          // Skip special rows (exact class match) - they'll be styled separately below
          // Note: "hover:bg-gray-50" is NOT the same as "bg-gray-50"
          if (classes.includes('bg-gray-50') || classes.includes('bg-green-50/50') || classes.includes('bg-green-50') || classes.includes('bg-red-50/50') || classes.includes('bg-red-50') || (cls.includes('bg-gradient-to-r') && cls.includes('purple'))) return;

          tr.querySelectorAll('td').forEach((td, i) => {
            if (i === 0) {
              td.setAttribute('style', `font-size:11px; color:#111827; padding:6px 10px; ${cellBorder} white-space:nowrap;`);
            } else {
              td.setAttribute('style', `font-size:11px; color:#374151; text-align:right; padding:6px 10px; ${cellBorder} white-space:nowrap;`);
            }
          });
        });
      });

      // 2) Style special rows with colored backgrounds
      root.querySelectorAll('tr').forEach(tr => {
        const cls = tr.getAttribute('class') || '';
        const classes = cls.split(/\s+/);

        // Non-paid / Paid subtotal rows (exact class match, not hover:bg-gray-50)
        if (classes.includes('bg-gray-50')) {
          const firstTd = tr.querySelector('td');
          if (firstTd) {
            const text = firstTd.textContent.trim().toLowerCase();

            // TOTAL row (highest priority - green)
            if (text === 'total') {
              tr.querySelectorAll('td').forEach((td, i) => {
                if (i === 0) {
                  td.setAttribute('style', `background:#dcfce7; font-weight:800; font-size:11px; text-transform:uppercase; color:#166534; padding:8px 10px; ${cellBorder} border-top:2px solid #86efac;`);
                } else {
                  td.setAttribute('style', `background:#dcfce7; font-weight:800; font-size:11px; text-align:right; color:#166534; padding:6px 10px; ${cellBorder} border-top:2px solid #86efac;`);
                }
              });
            }
            // NON-PAID or PAID subtotal rows (blue)
            else if (text.includes('non') || text.includes('paid')) {
              tr.querySelectorAll('td').forEach((td, i) => {
                if (i === 0) {
                  td.setAttribute('style', `background:#dbeafe; font-weight:700; font-size:11px; text-transform:uppercase; color:#1e40af; padding:8px 10px; ${cellBorder} border-top:2px solid #93c5fd;`);
                } else {
                  td.setAttribute('style', `background:#dbeafe; font-weight:700; font-size:11px; text-align:right; color:#1e40af; padding:6px 10px; ${cellBorder} border-top:2px solid #93c5fd;`);
                }
              });
            }
          }
        }

        // Total Revenue row (green) - matches bg-green-50/50 or bg-green-50
        if (cls.includes('bg-green-50') && !cls.includes('hover:bg-green-50')) {
          tr.querySelectorAll('td').forEach((td, i) => {
            if (i === 0) {
              td.setAttribute('style', `background:#dcfce7; font-weight:700; font-size:11px; color:#166534; padding:8px 10px; ${cellBorder}`);
            } else {
              td.setAttribute('style', `background:#dcfce7; font-weight:700; font-size:11px; text-align:right; color:#166534; padding:6px 10px; ${cellBorder}`);
            }
          });
        }

        // Total Cost row (red) - matches bg-red-50/50 or bg-red-50
        if (cls.includes('bg-red-50') && !cls.includes('hover:bg-red-50')) {
          tr.querySelectorAll('td').forEach((td, i) => {
            if (i === 0) {
              td.setAttribute('style', `background:#fee2e2; font-weight:700; font-size:11px; color:#991b1b; padding:8px 10px; ${cellBorder}`);
            } else {
              td.setAttribute('style', `background:#fee2e2; font-weight:700; font-size:11px; text-align:right; color:#991b1b; padding:6px 10px; ${cellBorder}`);
            }
          });
        }

        // ROI row (purple)
        if (cls.includes('bg-gradient-to-r') && cls.includes('purple')) {
          tr.querySelectorAll('td').forEach((td, i) => {
            if (i === 0) {
              td.setAttribute('style', `background:#ede9fe; font-weight:800; font-size:11px; color:#6b21a8; padding:8px 10px; ${cellBorder} border-top:2px solid #c4b5fd;`);
            } else {
              td.setAttribute('style', `background:#ede9fe; font-weight:800; font-size:11px; text-align:right; color:#6b21a8; padding:6px 10px; ${cellBorder} border-top:2px solid #c4b5fd;`);
            }
          });
        }

        // Remove cursor-pointer from all rows
        if (cls.includes('cursor-pointer')) {
          tr.setAttribute('class', cls.replace('cursor-pointer', ''));
        }
      });

      // 3) Color "Cost: ₺XX" sub-lines in red within google/cpc and facebook/cpc cells
      root.querySelectorAll('td').forEach(td => {
        const divs = td.querySelectorAll('div');
        divs.forEach(div => {
          const text = div.textContent.trim();
          if (text.startsWith('Cost:')) {
            div.setAttribute('style', 'color:#dc2626; font-size:10px; margin-top:2px; font-weight:500;');
          }
        });
      });

      tableHtml = root.innerHTML;

      const html = `
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Combined Traffic Sources</title>
            <style>
              /* Email table base (Gmail friendly) */
              table { border-collapse: collapse; width: 100%; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
              input, select, button, svg { display: none !important; }
            </style>
          </head>
          <body style="margin:0; padding:0; background:#f3f4f6; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="border-collapse:collapse;">
              <tr>
                <td align="center" style="padding:24px 16px;">
                  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="max-width:960px; margin:0 auto; border-collapse:collapse;">
                    <!-- Logo -->
                    <tr>
                      <td style="padding-bottom:20px; text-align:center;">
                        <img
                          src="https://platform.glowytics.com/Calisma-Yuzeyi-20@5x-4.png"
                          alt="Glowytics"
                          style="height:28px;width:auto;display:inline-block;"
                        />
                      </td>
                    </tr>
                    <!-- White card wrapper -->
                    <tr>
                      <td>
                        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background:#ffffff; border-radius:12px; border:1px solid #e5e7eb; border-collapse:collapse;">
                          <!-- Card header -->
                          <tr>
                            <td style="padding:20px 24px 12px 24px; border-bottom:1px solid #e5e7eb;">
                              <h1 style="margin:0; font-size:18px; font-weight:700; color:#111827;">
                                Combined Traffic Sources
                              </h1>
                              ${
                                selectedRangeLabel
                                  ? `<p style="margin:4px 0 0 0; font-size:12px; color:#6b7280;">
                                      ${selectedRangeLabel}
                                    </p>`
                                  : ''
                              }
                            </td>
                          </tr>
                          <!-- Card body -->
                          <tr>
                            <td style="padding:20px 24px;">
                              <div style="overflow:auto;">
                                ${tableHtml}
                              </div>
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                    <!-- Footer -->
                    <tr>
                      <td style="padding-top:16px; text-align:center;">
                        <p style="margin:0; font-size:11px; color:#9ca3af;">
                          Powered by <a href="https://glowytics.com" style="color:#6366f1; text-decoration:none;">Glowytics</a>
                        </p>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </body>
        </html>
      `;

      const currentBrandName = localStorage.getItem('currentBrandName') || '';
      const subject = `${currentBrandName ? currentBrandName + ' – ' : ''}Combined Traffic Sources – ${selectedRangeLabel || ''}`.trim();

      const res = await fetch('https://api.glowytics.com/api/email/sendgrid-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({
          to: reportEmail,
          subject: subject || 'Combined Traffic Sources',
          html
        })
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || `HTTP ${res.status}`);
      }

      setSendReportStatus({
        type: 'success',
        message: 'Test raporu e‑postası gönderildi.'
      });
    } catch (err) {
      console.error('Error sending combined traffic report email:', err);
      setSendReportStatus({
        type: 'error',
        message: 'E‑posta gönderilirken bir hata oluştu.'
      });
    } finally {
      setIsSendingReport(false);
    }
  };

  const fetchCombinedData = async () => {
    try {
      setLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage (currentBrandId)');
        setLoading(false);
        return;
      }

      const { startDate, endDate } = getDateRangeForFilter(dateRange);

      // GA request
      const gaDateRange = getApiDateRangeParam(dateRange);
      let gaUrl = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=${gaDateRange}`;
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        gaUrl += `&startDate=${formatDateForAPI(customStartDate)}&endDate=${formatDateForAPI(
          customEndDate
        )}`;
      }

      const gaPromise = fetch(gaUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });

      // Adjust request (date filtered directly in backend)
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', formatDateForAPI(startDate));
      if (endDate) params.set('endDate', formatDateForAPI(endDate));
      params.set('limit', '50000');

      const adjustUrl = `https://api.glowytics.com/api/adjust/performance?${params.toString()}`;
      const adjustPromise = fetch(adjustUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });

      const [gaRes, adjustRes] = await Promise.all([gaPromise, adjustPromise]);
      if (!gaRes.ok) {
        throw new Error(`GA API error: ${gaRes.status} ${gaRes.statusText}`);
      }
      if (!adjustRes.ok) {
        throw new Error(`Adjust API error: ${adjustRes.status} ${adjustRes.statusText}`);
      }

      const gaJson = await gaRes.json();
      const adjustJson = await adjustRes.json();

      const gaDetailed = gaJson && Array.isArray(gaJson.detailedData) ? gaJson.detailedData : [];
      const adjustRaw =
        adjustJson && adjustJson.ok && Array.isArray(adjustJson.rows)
          ? adjustJson.rows
          : Array.isArray(adjustJson.rows)
          ? adjustJson.rows
          : Array.isArray(adjustJson.data)
          ? adjustJson.data
          : [];
      // Aggregate GA by source / medium (with per-date breakdown)
      const gaGroups = {};
      gaDetailed.forEach((item) => {
        if (!item.date) return;
        const d = new Date(item.date);
        if (d < startDate || d > endDate) return;
        // Platform filter for GA detailed rows
        const platform = (item.platform || '').toString().toLowerCase();
        if (selectedPlatform === 'web' && platform && platform !== 'web') return;
        if (selectedPlatform === 'ios' && platform && platform !== 'ios') return;
        if (selectedPlatform === 'android' && platform && platform !== 'android') return;
        if (
          selectedPlatform === 'app' &&
          platform &&
          platform !== 'ios' &&
          platform !== 'android'
        ) {
          return;
        }
        const dateKey = d.toISOString().split('T')[0];
        const source = (item.source || 'unknown').toLowerCase();
        const medium = (item.medium || 'unknown').toLowerCase();
        const comboKey = `${source} / ${medium}`;

        // Brand-specific bucketing: only for 062be473-3b2b-4bad-a2e3-a31995e02d57
        let labelKey = comboKey;
        if (brandId === '062be473-3b2b-4bad-a2e3-a31995e02d57') {
          const allowedCombos = new Set([
            'google / cpc',
            'facebook / cpc',
            'ig / paid',
            'meta / paidsocial_traffic',
            'rtbhouse / cpa',
            'criteo / display',
            'ig / social',
            'yandex / cpc',
            'rtbhouse / retargeting',
            'apple / search',
            'tiktok / paid'
          ]);
          const singleSourceLabels = new Set(['pinterest', 'bing']);

          if (allowedCombos.has(comboKey)) {
            labelKey = comboKey;
          } else if (medium === 'organic') {
            // All organic traffic (any source) together
            labelKey = 'organic';
          } else if (source === '(direct)' || source === 'direct') {
            // Direct traffic bucket
            labelKey = 'direct';
          } else if (singleSourceLabels.has(source)) {
            // pinterest, bing as their own rows
            labelKey = source;
          } else {
            labelKey = 'other';
          }
        }

        if (!gaGroups[labelKey]) {
          gaGroups[labelKey] = {
            label: labelKey,
            revenue: 0,
            sessions: 0,
            installs: 0,
            conversions: 0,
            conversionRate: 0,
            perDate: {}
          };
        }

        const revenueVal = Number(item.totalRevenue || 0);
        const sessionsVal = Number(item.sessions || 0);
        const conversionsVal = Number(
          item.transactions || item.conversions || item.purchases || 0
        );

        gaGroups[labelKey].revenue += revenueVal;
        gaGroups[labelKey].sessions += sessionsVal;
        // GA tarafında install yok, 0 kalacak; conversions için transactions / purchases kabul ediyoruz
        gaGroups[labelKey].conversions += conversionsVal;

        if (dateKey) {
          if (!gaGroups[labelKey].perDate[dateKey]) {
            gaGroups[labelKey].perDate[dateKey] = {
              date: dateKey,
              revenue: 0,
              sessions: 0,
              installs: 0,
              conversions: 0
            };
          }
          gaGroups[labelKey].perDate[dateKey].revenue += revenueVal;
          gaGroups[labelKey].perDate[dateKey].sessions += sessionsVal;
          gaGroups[labelKey].perDate[dateKey].conversions += conversionsVal;
        }
      });

      const gaRowsNew = Object.values(gaGroups).map((row) => {
        const sessions = Number(row.sessions || 0);
        const conversions = Number(row.conversions || 0);
        const conversionRate = sessions > 0 ? conversions / sessions : 0;
        return {
          ...row,
          conversionRate
        };
      });

      // Aggregate Adjust by channel (with per-date breakdown)
      // Bucket logic:
      // - "organic"       → its own row under Non‑paid
      // - paid whitelist  → individual rows under Paid
      // - everything else → single aggregated "Other" row under Non‑paid
      const adjGroups = {};
      const adjustPaidWhitelist = new Set([
        'tiktok for business',
        'pinterest',
        'app samurai',
        'facebook',
        'rtb_app',
        'reklam action',
        'adventure_app',
        'apple',
        'google ads'
      ]);

      adjustRaw.forEach((item) => {
        const rawLabel = item.channel || item.source_network || 'Unknown';
        const lcLabel = rawLabel.toLowerCase();

        let key;
        if (lcLabel === 'organic') {
          key = 'organic';
        } else if (adjustPaidWhitelist.has(lcLabel)) {
          key = lcLabel;
        } else {
          key = 'other';
        }

        const d = item.date ? new Date(item.date) : null;
        const dateKey = d ? d.toISOString().split('T')[0] : null;

        if (!adjGroups[key]) {
          adjGroups[key] = {
            label: key === 'other' ? 'Other' : rawLabel,
            revenue: 0,
            sessions: 0,
            installs: 0,
            conversions: 0,
            conversionRate: 0,
            perDate: {}
          };
        }
        const g = adjGroups[key];
        const revenueVal = Number(item.all_revenue ?? item.revenue ?? 0);
        const sessionsVal = Number(item.sessions ?? 0);
        const installsVal = Number(item.installs ?? 0);
        const conversionsVal = Number(item.purchases ?? item.events_count ?? 0);

        g.revenue += revenueVal;
        g.sessions += sessionsVal;
        g.installs += installsVal;
        g.conversions += conversionsVal;

        if (dateKey) {
          if (!g.perDate[dateKey]) {
            g.perDate[dateKey] = {
              date: dateKey,
              revenue: 0,
              sessions: 0,
              installs: 0,
              conversions: 0
            };
          }
          g.perDate[dateKey].revenue += revenueVal;
          g.perDate[dateKey].sessions += sessionsVal;
          g.perDate[dateKey].installs += installsVal;
          g.perDate[dateKey].conversions += conversionsVal;
        }
      });

      const adjustRowsNew = Object.values(adjGroups).map((row) => {
        const sessions = Number(row.sessions || 0);
        const conversions = Number(row.conversions || 0);
        const conversionRate = sessions > 0 ? conversions / sessions : 0;
        return {
          ...row,
          conversionRate
        };
      });

      // Fetch Google Ads and Meta Ads daily cost data
      const gaDateRangeParam = getApiDateRangeParam(dateRange);
      let googleAdsUrl = `https://api.glowytics.com/api/google-ads/unified-googleads-ga4-performance?dateRange=${gaDateRangeParam}`;
      let metaAdsUrl = `https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?dateRange=${gaDateRangeParam}`;
      
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const startStr = formatDateForAPI(customStartDate);
        const endStr = formatDateForAPI(customEndDate);
        googleAdsUrl = `https://api.glowytics.com/api/google-ads/unified-googleads-ga4-performance?dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
        metaAdsUrl = `https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?dateRange=custom&startDate=${startStr}&endDate=${endStr}`;
      }

      const [googleAdsRes, metaAdsRes] = await Promise.all([
        fetch(googleAdsUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        }),
        fetch(metaAdsUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        })
      ]);

      const googleAdsJson = googleAdsRes.ok ? await googleAdsRes.json() : [];
      const metaAdsJson = metaAdsRes.ok ? await metaAdsRes.json() : [];

      // Helper to normalize date to YYYY-MM-DD string
      const normalizeDateStr = (d) => {
        if (!d) return null;
        const dateObj = new Date(d);
        if (isNaN(dateObj.getTime())) return null;
        return dateObj.toISOString().split('T')[0];
      };

      // Aggregate Google Ads by date
      const googleAdsByDate = {};
      (Array.isArray(googleAdsJson) ? googleAdsJson : []).forEach((item) => {
        const dateKey = normalizeDateStr(item.date);
        if (!dateKey) return;
        if (!googleAdsByDate[dateKey]) {
          googleAdsByDate[dateKey] = { date: dateKey, cost: 0 };
        }
        googleAdsByDate[dateKey].cost += Number(item.cost || 0);
      });

      // Aggregate Meta Ads by date
      const metaAdsByDate = {};
      (Array.isArray(metaAdsJson) ? metaAdsJson : []).forEach((item) => {
        const dateKey = normalizeDateStr(item.date);
        if (!dateKey) return;
        if (!metaAdsByDate[dateKey]) {
          metaAdsByDate[dateKey] = { date: dateKey, cost: 0 };
        }
        metaAdsByDate[dateKey].cost += Number(item.spend || 0);
      });

      console.log('📊 Google Ads daily cost data:', Object.values(googleAdsByDate));
      console.log('📊 Meta Ads daily cost data:', Object.values(metaAdsByDate));
      setDailyGoogleAdsData(Object.values(googleAdsByDate));
      setDailyMetaAdsData(Object.values(metaAdsByDate));
      setGaRows(gaRowsNew);
      setAdjustRows(adjustRowsNew);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching combined GA + Adjust data:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCombinedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, customStartDate, customEndDate, selectedPlatform]);

  // Fetch monthly data (all data, no filters)
  const fetchMonthlyData = async () => {
    try {
      setMonthlyLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.log('No brand ID found in localStorage (currentBrandId)');
        setMonthlyLoading(false);
        return;
      }

      // Fetch ALL data for monthly view
      const gaUrl = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=all`;
      const gaPromise = fetch(gaUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });

      const adjustUrl = `https://api.glowytics.com/api/adjust/performance/all?limit=500000`;
      const adjustPromise = fetch(adjustUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include'
      });

      const [gaRes, adjustRes] = await Promise.all([gaPromise, adjustPromise]);
      if (!gaRes.ok) throw new Error(`GA API error: ${gaRes.status}`);
      if (!adjustRes.ok) throw new Error(`Adjust API error: ${adjustRes.status}`);

      const gaJson = await gaRes.json();
      const adjustJson = await adjustRes.json();

      const gaDetailed = gaJson && Array.isArray(gaJson.detailedData) ? gaJson.detailedData : [];
      const adjustRaw = adjustJson?.rows || adjustJson?.data || [];

      // Aggregate GA by source/medium and MONTH (only platform = 'web')
      const gaGroups = {};
      gaDetailed.forEach((item) => {
        if (!item.date) return;
        // Only include web platform data
        const platform = (item.platform || '').toLowerCase();
        if (platform && platform !== 'web') return;
        const d = new Date(item.date);
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const source = (item.source || 'unknown').toLowerCase();
        const medium = (item.medium || 'unknown').toLowerCase();
        const comboKey = `${source} / ${medium}`;

        // Same bucketing logic as daily
        let labelKey = comboKey;
        if (brandId === '062be473-3b2b-4bad-a2e3-a31995e02d57') {
          const allowedCombos = new Set([
            'google / cpc', 'facebook / cpc', 'ig / paid', 'meta / paidsocial_traffic',
            'rtbhouse / cpa', 'criteo / display', 'ig / social', 'yandex / cpc',
            'rtbhouse / retargeting', 'apple / search', 'tiktok / paid'
          ]);
          const singleSourceLabels = new Set(['pinterest', 'bing']);
          if (allowedCombos.has(comboKey)) labelKey = comboKey;
          else if (medium === 'organic') labelKey = 'organic';
          else if (source === '(direct)' || source === 'direct') labelKey = 'direct';
          else if (singleSourceLabels.has(source)) labelKey = source;
          else labelKey = 'other';
        }

        if (!gaGroups[labelKey]) {
          gaGroups[labelKey] = { label: labelKey, revenue: 0, sessions: 0, installs: 0, conversions: 0, perMonth: {} };
        }

        const revenueVal = Number(item.totalRevenue || 0);
        const sessionsVal = Number(item.sessions || 0);
        const conversionsVal = Number(item.transactions || item.conversions || item.purchases || 0);

        gaGroups[labelKey].revenue += revenueVal;
        gaGroups[labelKey].sessions += sessionsVal;
        gaGroups[labelKey].conversions += conversionsVal;

        if (!gaGroups[labelKey].perMonth[monthKey]) {
          gaGroups[labelKey].perMonth[monthKey] = { month: monthKey, revenue: 0, sessions: 0, installs: 0, conversions: 0 };
        }
        gaGroups[labelKey].perMonth[monthKey].revenue += revenueVal;
        gaGroups[labelKey].perMonth[monthKey].sessions += sessionsVal;
        gaGroups[labelKey].perMonth[monthKey].conversions += conversionsVal;
      });

      const gaMonthlyRowsNew = Object.values(gaGroups).map((row) => {
        const sessions = Number(row.sessions || 0);
        const conversions = Number(row.conversions || 0);
        return { ...row, conversionRate: sessions > 0 ? conversions / sessions : 0 };
      });

      // Aggregate Adjust by channel and MONTH (raw data from /performance/all endpoint)
      const adjGroups = {};
      const adjustPaidWhitelist = new Set([
        'tiktok for business', 'pinterest', 'app samurai', 'facebook', 'rtb_app',
        'reklam action', 'adventure_app', 'apple', 'google ads'
      ]);

      adjustRaw.forEach((item) => {
        const rawLabel = item.channel || item.source_network || 'Unknown';
        const lcLabel = rawLabel.toLowerCase();
        let key = lcLabel === 'organic' ? 'organic' : adjustPaidWhitelist.has(lcLabel) ? lcLabel : 'other';

        const d = item.date ? new Date(item.date) : null;
        const monthKey = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : null;

        if (!adjGroups[key]) {
          adjGroups[key] = { label: key === 'other' ? 'Other' : rawLabel, revenue: 0, sessions: 0, installs: 0, conversions: 0, perMonth: {} };
        }

        const g = adjGroups[key];
        const revenueVal = Number(item.all_revenue ?? item.revenue ?? 0);
        const sessionsVal = Number(item.sessions ?? 0);
        const installsVal = Number(item.installs ?? 0);
        const conversionsVal = Number(item.purchases ?? item.events_count ?? 0);

        g.revenue += revenueVal;
        g.sessions += sessionsVal;
        g.installs += installsVal;
        g.conversions += conversionsVal;

        if (monthKey) {
          if (!g.perMonth[monthKey]) {
            g.perMonth[monthKey] = { month: monthKey, revenue: 0, sessions: 0, installs: 0, conversions: 0 };
          }
          g.perMonth[monthKey].revenue += revenueVal;
          g.perMonth[monthKey].sessions += sessionsVal;
          g.perMonth[monthKey].installs += installsVal;
          g.perMonth[monthKey].conversions += conversionsVal;
        }
      });

      const adjustMonthlyRowsNew = Object.values(adjGroups).map((row) => {
        const sessions = Number(row.sessions || 0);
        const conversions = Number(row.conversions || 0);
        return { ...row, conversionRate: sessions > 0 ? conversions / sessions : 0 };
      });

      setGaMonthlyRows(gaMonthlyRowsNew);
      setAdjustMonthlyRows(adjustMonthlyRowsNew);
      setMonthlyLoading(false);
    } catch (err) {
      console.error('❌ Error fetching monthly data:', err);
      setMonthlyLoading(false);
    }
  };

  // Fetch monthly data when switching to monthly tab
  useEffect(() => {
    if (activeSection === 'monthly' && gaMonthlyRows.length === 0 && adjustMonthlyRows.length === 0) {
      fetchMonthlyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Fetch Monthly Performance Trends (Google + Meta Ads)
  const fetchMonthlyPerformanceData = async () => {
    try {
      setIsLoadingMonthlyPerformance(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        setIsLoadingMonthlyPerformance(false);
        return;
      }

      // Add timestamp to bypass cache and get fresh data
      const monthlyResp = await fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance-monthly?dateRange=all&_t=${Date.now()}`, {
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
      });
      const monthlyRows = monthlyResp.ok ? await monthlyResp.json() : [];
      console.log('Total monthly rows from API:', monthlyRows.length);

      // Process data by platform - same logic as Overview2
      const googleData = [];
      const metaData = [];
      for (const row of (Array.isArray(monthlyRows) ? monthlyRows : [])) {
        const base = {
          date: row.month, // monthly timestamp
          ga4_sessions: row.ga4_sessions || 0,
          ga4_total_revenue: row.ga4_total_revenue || 0,
          ga4_purchases: row.ga4_purchases || 0,
          conversions: 0,
          conversion_value: 0,
          clicks: 0,
          cost: 0,
          spend: 0,
        };
        // Filter by channel field like Overview2 does
        if ((row.channel || '').toLowerCase() === 'google ads') {
          googleData.push({
            ...base,
            clicks: row.google_clicks || 0,
            cost: row.google_cost || 0,
            conversions: row.google_conversions || 0,
            conversion_value: row.google_conversion_value || 0,
            campaign_name: row.campaign_name,
          });
        } else if ((row.channel || '').toLowerCase() === 'meta ads') {
          metaData.push({
            ...base,
            clicks: row.meta_clicks || 0,
            spend: row.meta_cost || 0,
            conversions: row.meta_conversions || 0,
            conversion_value: row.meta_conversion_value || 0,
            campaign_name: row.campaign_name,
          });
        }
      }

      // Process platform data
      const getMonthKeyLocal = (dateValue) => {
        if (!dateValue) return '';
        const s = String(dateValue).trim();
        // Try YYYY-MM or YYYY-MM-DD format
        const m = s.match(/^(\d{4})-(\d{2})/);
        if (m) return `${m[1]}-${m[2]}`;
        // Try parsing as Date object (handles ISO strings, timestamps, etc.)
        try {
          const d = new Date(dateValue);
          if (!isNaN(d.getTime())) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          }
        } catch (e) {}
        return '';
      };

      // Debug: log first few rows to see data structure
      console.log('Monthly performance rows sample:', monthlyRows.slice(0, 3));
      console.log('Google data count:', googleData.length, 'Meta data count:', metaData.length);
      if (googleData.length > 0) console.log('Google data months:', [...new Set(googleData.map(d => getMonthKeyLocal(d.date)))]);
      if (metaData.length > 0) console.log('Meta data months:', [...new Set(metaData.map(d => getMonthKeyLocal(d.date)))]);
      const formatMonthLabelPlusOne = (monthKey) => {
        if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return monthKey || '';
        const [yy, mm] = monthKey.split('-').map(Number);
        const date = new Date(yy, (mm - 1) + 1, 1);
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
      };

      const processPlatformData = (data) => {
        if (!Array.isArray(data) || data.length === 0) return null;
        const monthOrder = [];
        const monthlyTotals = data.reduce((acc, item) => {
          const month = getMonthKeyLocal(item.date);
          if (!month) return acc;
          if (!acc[month]) {
            acc[month] = { cost: 0, clicks: 0, conversions: 0, revenue: 0, ga4_sessions: 0, ga4_total_revenue: 0, ga4_purchases: 0 };
            monthOrder.push(month);
          }
          acc[month].cost += (item.cost || item.spend || 0);
          acc[month].clicks += item.clicks || 0;
          acc[month].conversions += item.conversions || 0;
          acc[month].revenue += item.conversion_value || 0;
          acc[month].ga4_sessions += item.ga4_sessions || 0;
          acc[month].ga4_total_revenue += item.ga4_total_revenue || 0;
          acc[month].ga4_purchases += item.ga4_purchases || 0;
          return acc;
        }, {});

        const platformTotals = monthOrder.reduce((acc, key) => {
          const m = monthlyTotals[key] || {};
          acc.cost += m.cost || 0;
          acc.clicks += m.clicks || 0;
          acc.conversions += m.conversions || 0;
          acc.revenue += m.revenue || 0;
          acc.ga4_sessions += m.ga4_sessions || 0;
          acc.ga4_total_revenue += m.ga4_total_revenue || 0;
          acc.ga4_purchases += m.ga4_purchases || 0;
          return acc;
        }, { cost: 0, clicks: 0, conversions: 0, revenue: 0, ga4_sessions: 0, ga4_total_revenue: 0, ga4_purchases: 0 });

        const monthsData = monthOrder.map((monthStr, index) => {
          const monthData = monthlyTotals[monthStr];
          const prevMonthStr = monthOrder[index + 1];
          const prevMonthData = prevMonthStr ? monthlyTotals[prevMonthStr] : null;
          const roas = monthData.cost > 0 ? monthData.revenue / monthData.cost : 0;
          let roasChange = null;
          if (prevMonthData) {
            const prevRoas = prevMonthData.cost > 0 ? prevMonthData.revenue / prevMonthData.cost : 0;
            if (prevRoas > 0) roasChange = ((roas - prevRoas) / prevRoas) * 100;
          }
          return {
            monthKey: monthStr, // Raw YYYY-MM format for matching
            month: formatMonthLabelPlusOne(monthStr),
            cost: monthData.cost,
            clicks: monthData.clicks,
            conversions: monthData.conversions,
            revenue: monthData.revenue,
            ga4_sessions: monthData.ga4_sessions,
            ga4_total_revenue: monthData.ga4_total_revenue,
            ga4_purchases: monthData.ga4_purchases,
            cpc: monthData.clicks > 0 ? monthData.cost / monthData.clicks : 0,
            cps: monthData.ga4_sessions > 0 ? monthData.cost / monthData.ga4_sessions : 0,
            cpa: monthData.conversions > 0 ? monthData.cost / monthData.conversions : 0,
            cr: monthData.clicks > 0 ? (monthData.conversions / monthData.clicks) * 100 : 0,
            ga4_roas: monthData.cost > 0 ? monthData.ga4_total_revenue / monthData.cost : 0,
            roas,
            roasChange,
          };
        });

        return {
          totals: {
            ...platformTotals,
            cpc: platformTotals.clicks > 0 ? platformTotals.cost / platformTotals.clicks : 0,
            cps: platformTotals.ga4_sessions > 0 ? platformTotals.cost / platformTotals.ga4_sessions : 0,
            cpa: platformTotals.conversions > 0 ? platformTotals.cost / platformTotals.conversions : 0,
            cr: platformTotals.clicks > 0 ? (platformTotals.conversions / platformTotals.clicks) * 100 : 0,
            ga4_roas: platformTotals.cost > 0 ? platformTotals.ga4_total_revenue / platformTotals.cost : 0,
            roas: platformTotals.cost > 0 ? platformTotals.revenue / platformTotals.cost : 0,
          },
          months: monthsData,
        };
      };

      const googleProcessed = processPlatformData(googleData);
      const metaProcessed = processPlatformData(metaData);

      if (!googleProcessed && !metaProcessed) {
        setMonthlyPerformanceData(null);
        setIsLoadingMonthlyPerformance(false);
        return;
      }

      const overallTotal = {
        cost: (googleProcessed?.totals.cost || 0) + (metaProcessed?.totals.cost || 0),
        clicks: (googleProcessed?.totals.clicks || 0) + (metaProcessed?.totals.clicks || 0),
        conversions: (googleProcessed?.totals.conversions || 0) + (metaProcessed?.totals.conversions || 0),
        revenue: (googleProcessed?.totals.revenue || 0) + (metaProcessed?.totals.revenue || 0),
        ga4_sessions: (googleProcessed?.totals.ga4_sessions || 0) + (metaProcessed?.totals.ga4_sessions || 0),
        ga4_total_revenue: (googleProcessed?.totals.ga4_total_revenue || 0) + (metaProcessed?.totals.ga4_total_revenue || 0),
        ga4_purchases: (googleProcessed?.totals.ga4_purchases || 0) + (metaProcessed?.totals.ga4_purchases || 0),
      };
      overallTotal.cpc = overallTotal.clicks > 0 ? overallTotal.cost / overallTotal.clicks : 0;
      overallTotal.cps = overallTotal.ga4_sessions > 0 ? overallTotal.cost / overallTotal.ga4_sessions : 0;
      overallTotal.cpa = overallTotal.conversions > 0 ? overallTotal.cost / overallTotal.conversions : 0;
      overallTotal.cr = overallTotal.clicks > 0 ? (overallTotal.conversions / overallTotal.clicks) * 100 : 0;
      overallTotal.ga4_roas = overallTotal.cost > 0 ? overallTotal.ga4_total_revenue / overallTotal.cost : 0;
      overallTotal.roas = overallTotal.cost > 0 ? overallTotal.revenue / overallTotal.cost : 0;

      setMonthlyPerformanceData({
        'Google Ads': googleProcessed,
        'Meta Ads': metaProcessed,
        'Total': overallTotal,
      });
      setIsLoadingMonthlyPerformance(false);
    } catch (err) {
      console.error('Error fetching monthly performance data:', err);
      setIsLoadingMonthlyPerformance(false);
    }
  };

  // Fetch monthly performance when switching to monthly tab
  useEffect(() => {
    if (activeSection === 'monthly' && !monthlyPerformanceData) {
      fetchMonthlyPerformanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Toggle platform expansion
  const togglePlatformExpand = (platformName) => {
    setExpandedPlatforms(prev =>
      prev.includes(platformName)
        ? prev.filter(p => p !== platformName)
        : [...prev, platformName]
    );
  };

  // Format helpers for Monthly Performance Trends
  const formatCurrencyCompact = (value) => {
    const n = Number(value) || 0;
    if (n >= 1_000_000) return `₺${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `₺${Math.round(n / 1_000)}K`;
    return `₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
  };
  const formatCurrencyUnit = (value) => {
    const n = Number(value) || 0;
    return `₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
  };
  const formatCurrencyUnitTwo = (value) => {
    const n = Number(value) || 0;
    return `₺${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatStat = (value) => {
    const n = Number(value) || 0;
    return n.toLocaleString('tr-TR');
  };

  // Monthly filtered/sorted rows
  const filteredGaMonthlyRows = useMemo(() => {
    const metric = selectedMonthlyMetric;
    return [...gaMonthlyRows].sort((a, b) => {
      const aVal = metric === 'revenue' ? a.revenue : metric === 'sessions' ? a.sessions : metric === 'installs' ? a.installs : a.conversionRate;
      const bVal = metric === 'revenue' ? b.revenue : metric === 'sessions' ? b.sessions : metric === 'installs' ? b.installs : b.conversionRate;
      return bVal - aVal;
    });
  }, [gaMonthlyRows, selectedMonthlyMetric]);

  const filteredAdjustMonthlyRows = useMemo(() => {
    const metric = selectedMonthlyMetric;
    return [...adjustMonthlyRows].sort((a, b) => {
      const aVal = metric === 'revenue' ? a.revenue : metric === 'sessions' ? a.sessions : metric === 'installs' ? a.installs : a.conversionRate;
      const bVal = metric === 'revenue' ? b.revenue : metric === 'sessions' ? b.sessions : metric === 'installs' ? b.installs : b.conversionRate;
      return bVal - aVal;
    });
  }, [adjustMonthlyRows, selectedMonthlyMetric]);

  // Monthly date columns
  const gaMonthlyColumns = useMemo(() => {
    const monthSet = new Set();
    gaMonthlyRows.forEach((row) => {
      if (row.perMonth) Object.keys(row.perMonth).forEach((m) => monthSet.add(m));
    });
    return Array.from(monthSet).sort();
  }, [gaMonthlyRows]);

  const adjustMonthlyColumns = useMemo(() => {
    const monthSet = new Set();
    adjustMonthlyRows.forEach((row) => {
      if (row.perMonth) Object.keys(row.perMonth).forEach((m) => monthSet.add(m));
    });
    return Array.from(monthSet).sort();
  }, [adjustMonthlyRows]);

  const filteredGaRows = useMemo(() => {
    const metric = selectedMetric;
    const q = searchTerm.toLowerCase();
    const base = !q ? gaRows : gaRows.filter((r) => r.label.toLowerCase().includes(q));
    return [...base].sort(
      (a, b) => getRowMetricValue(b, metric) - getRowMetricValue(a, metric)
    );
  }, [gaRows, searchTerm, selectedMetric]);

  const filteredAdjustRows = useMemo(() => {
    const metric = selectedMetric;
    const q = searchTerm.toLowerCase();
    const base = !q ? adjustRows : adjustRows.filter((r) => r.label.toLowerCase().includes(q));
    return [...base].sort(
      (a, b) => getRowMetricValue(b, metric) - getRowMetricValue(a, metric)
    );
  }, [adjustRows, searchTerm, selectedMetric]);

  const gaDateColumns = useMemo(() => {
    const dateSet = new Set();
    gaRows.forEach((row) => {
      if (row.perDate) {
        Object.keys(row.perDate).forEach((d) => dateSet.add(d));
      }
    });
    return Array.from(dateSet).sort();
  }, [gaRows]);

  const adjustDateColumns = useMemo(() => {
    const dateSet = new Set();
    adjustRows.forEach((row) => {
      if (row.perDate) {
        Object.keys(row.perDate).forEach((d) => dateSet.add(d));
      }
    });
    return Array.from(dateSet).sort();
  }, [adjustRows]);

  if (loading && gaRows.length === 0 && adjustRows.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="loading-logo-container">
          <img src="/logoicon.png" alt="Glowytics Logo" className="loading-logo" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Combined Traffic Sources</h1>
          {selectedRangeLabel && (
            <p className="text-sm text-gray-500 mt-1">Date Range: {selectedRangeLabel}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
            {[
              ['yesterday', 'Yesterday'],
              ['last7', '7 Days'],
              ['last30', '30 Days'],
              ['thisMonth', 'This Month'],
              ['lastMonth', 'Last Month'],
              ['last90', '90 Days'],
              ['all', 'All Data']
            ].map(([value, label]) => (
              <button
                key={value}
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  dateRange === value
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}
                onClick={() => setDateRange(value)}
              >
                {label}
              </button>
            ))}
            <button
              className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                dateRange === 'custom'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-white hover:text-gray-800'
              }`}
              onClick={() => setIsCustomDateOpen(true)}
            >
              Custom Range
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Metric:</span>
            <select
              className="px-2 py-1 text-sm border rounded-lg bg-white text-gray-700"
              value={selectedMetric}
              onChange={(e) => setSelectedMetric(e.target.value)}
            >
              <option value="revenue">Revenue</option>
              <option value="sessions">Sessions</option>
              <option value="installs">Installs</option>
              <option value="conversion">Conversion Rate</option>
            </select>
          </div>
          <button
            className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
            onClick={fetchCombinedData}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1.5 inline-flex gap-1.5">
        <button
          onClick={() => setActiveSection('daily')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
            activeSection === 'daily'
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/25'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Daily
        </button>
        <button
          onClick={() => setActiveSection('monthly')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
            activeSection === 'monthly'
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/25'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
          }`}
        >
          <CalendarDays className="w-4 h-4" />
          Monthly
        </button>
      </div>

      {/* Daily Section */}
      {activeSection === 'daily' && (
      <>
      {/* Email snapshot wrapper - includes Combined Summary + GA + Adjust tables */}
      <div id="combined-traffic-email-snapshot" className="space-y-6">

      {/* Combined Summary Table - GA + Adjust Totals */}
      <Card className="border border-gray-200 rounded-xl shadow-sm">
        <div className="p-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <img src="/GA4logoicon.svg" alt="GA" className="w-4 h-4" />
              <span className="text-gray-400">+</span>
              <img src="/adjustlogopng.png" alt="Adjust" className="w-4 h-4" />
            </div>
            <h2 className="font-semibold text-gray-800">Combined Summary (GA + Adjust)</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200 border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-left text-[11px] text-gray-600">
                <th className="px-3 py-2 font-semibold border-b border-gray-200">SOURCE</th>
                {(() => {
                  // Combine all dates from both GA and Adjust
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => (
                    <th
                      key={date}
                      className="px-3 py-2 font-semibold text-right whitespace-nowrap border-b border-l border-gray-200"
                    >
                      {date}
                    </th>
                  ));
                })()}
              </tr>
            </thead>
            <tbody>
              {/* GA Total Row */}
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <img src="/GA4logoicon.svg" alt="GA" className="w-4 h-4" />
                    <span>Google Analytics</span>
                  </div>
                </td>
                {(() => {
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => {
                    const metric = selectedMetric;
                    if (metric === 'conversion') {
                      let totalSessions = 0;
                      let totalConversions = 0;
                      filteredGaRows.forEach((r) => {
                        const per = r.perDate?.[date];
                        if (!per) return;
                        totalSessions += Number(per.sessions || 0);
                        totalConversions += Number(per.conversions || 0);
                      });
                      const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                      return (
                        <td key={date} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                          {totalSessions === 0 ? '-' : formatMetricAggregate(rate, metric)}
                        </td>
                      );
                    }
                    let total = 0;
                    filteredGaRows.forEach((r) => {
                      const per = r.perDate?.[date];
                      if (!per) return;
                      if (metric === 'revenue') total += Number(per.revenue || 0);
                      else if (metric === 'sessions') total += Number(per.sessions || 0);
                      else if (metric === 'installs') total += Number(per.installs || 0);
                    });
                    return (
                      <td key={date} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                        {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                      </td>
                    );
                  });
                })()}
              </tr>
              {/* Adjust Total Row */}
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <img src="/adjustlogopng.png" alt="Adjust" className="w-4 h-4" />
                    <span>Adjust</span>
                  </div>
                </td>
                {(() => {
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => {
                    const metric = selectedMetric;
                    if (metric === 'conversion') {
                      let totalSessions = 0;
                      let totalConversions = 0;
                      filteredAdjustRows.forEach((r) => {
                        const per = r.perDate?.[date];
                        if (!per) return;
                        totalSessions += Number(per.sessions || 0);
                        totalConversions += Number(per.conversions || 0);
                      });
                      const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                      return (
                        <td key={date} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                          {totalSessions === 0 ? '-' : formatMetricAggregate(rate, metric)}
                        </td>
                      );
                    }
                    let total = 0;
                    filteredAdjustRows.forEach((r) => {
                      const per = r.perDate?.[date];
                      if (!per) return;
                      if (metric === 'revenue') total += Number(per.revenue || 0);
                      else if (metric === 'sessions') total += Number(per.sessions || 0);
                      else if (metric === 'installs') total += Number(per.installs || 0);
                    });
                    return (
                      <td key={date} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                        {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                      </td>
                    );
                  });
                })()}
              </tr>
              {/* Google Ads Cost Row */}
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
                    <span>Google Ads (Cost)</span>
                  </div>
                </td>
                {(() => {
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => {
                    const found = dailyGoogleAdsData.find((d) => d.date === date);
                    const cost = found ? found.cost : 0;
                    return (
                      <td key={date} className="px-3 py-2.5 text-[12px] text-right text-red-600 border-t border-l border-gray-200">
                        {cost === 0 ? '-' : `₺${Math.round(cost).toLocaleString('tr-TR')}`}
                      </td>
                    );
                  });
                })()}
              </tr>
              {/* Meta Ads Cost Row */}
              <tr className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <img src="/6033716.png" alt="Meta Ads" className="w-4 h-4" />
                    <span>Meta Ads (Cost)</span>
                  </div>
                </td>
                {(() => {
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => {
                    const found = dailyMetaAdsData.find((d) => d.date === date);
                    const cost = found ? found.cost : 0;
                    return (
                      <td key={date} className="px-3 py-2.5 text-[12px] text-right text-red-600 border-t border-l border-gray-200">
                        {cost === 0 ? '-' : `₺${Math.round(cost).toLocaleString('tr-TR')}`}
                      </td>
                    );
                  });
                })()}
              </tr>
              {/* Total Revenue Row */}
              <tr className="bg-green-50/50">
                <td className="px-3 py-2.5 text-[12px] font-semibold text-green-700 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">💰</span>
                    <span>Total Revenue</span>
                  </div>
                </td>
                {(() => {
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => {
                    let gaRevenue = 0;
                    let adjRevenue = 0;
                    filteredGaRows.forEach((r) => {
                      const per = r.perDate?.[date];
                      if (per) gaRevenue += Number(per.revenue || 0);
                    });
                    filteredAdjustRows.forEach((r) => {
                      const per = r.perDate?.[date];
                      if (per) adjRevenue += Number(per.revenue || 0);
                    });
                    const totalRevenue = gaRevenue + adjRevenue;
                    return (
                      <td key={date} className="px-3 py-2.5 text-[12px] font-semibold text-right text-green-700 border-t border-l border-gray-200">
                        {totalRevenue === 0 ? '-' : `₺${Math.round(totalRevenue).toLocaleString('tr-TR')}`}
                      </td>
                    );
                  });
                })()}
              </tr>
              {/* Total Cost Row */}
              <tr className="bg-red-50/50">
                <td className="px-3 py-2.5 text-[12px] font-semibold text-red-700 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="text-red-600">➖</span>
                    <span>Total Cost</span>
                  </div>
                </td>
                {(() => {
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => {
                    const googleCost = dailyGoogleAdsData.find((d) => d.date === date)?.cost || 0;
                    const metaCost = dailyMetaAdsData.find((d) => d.date === date)?.cost || 0;
                    const totalCost = googleCost + metaCost;
                    return (
                      <td key={date} className="px-3 py-2.5 text-[12px] font-semibold text-right text-red-700 border-t border-l border-gray-200">
                        {totalCost === 0 ? '-' : `₺${Math.round(totalCost).toLocaleString('tr-TR')}`}
                      </td>
                    );
                  });
                })()}
              </tr>
              {/* ROI Row */}
              <tr className="bg-gradient-to-r from-purple-50 to-indigo-50">
                <td className="px-3 py-2.5 text-[12px] font-bold text-purple-700 border-t-2 border-gray-300">
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600">✖️</span>
                    <span>ROI</span>
                  </div>
                </td>
                {(() => {
                  const allDates = new Set([...gaDateColumns, ...adjustDateColumns]);
                  return Array.from(allDates).sort().map((date) => {
                    let gaRevenue = 0;
                    let adjRevenue = 0;
                    filteredGaRows.forEach((r) => {
                      const per = r.perDate?.[date];
                      if (per) gaRevenue += Number(per.revenue || 0);
                    });
                    filteredAdjustRows.forEach((r) => {
                      const per = r.perDate?.[date];
                      if (per) adjRevenue += Number(per.revenue || 0);
                    });
                    const totalRevenue = gaRevenue + adjRevenue;
                    const googleCost = dailyGoogleAdsData.find((d) => d.date === date)?.cost || 0;
                    const metaCost = dailyMetaAdsData.find((d) => d.date === date)?.cost || 0;
                    const totalCost = googleCost + metaCost;
                    const roi = totalCost > 0 ? totalRevenue / totalCost : 0;
                    return (
                      <td key={date} className="px-3 py-2.5 text-[12px] font-bold text-right text-purple-700 border-t-2 border-l border-gray-300">
                        {totalCost === 0 ? '-' : `${roi.toFixed(2)}x`}
                      </td>
                    );
                  });
                })()}
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Two stacked tables: GA and Adjust */}
        <Card className="border border-gray-200 rounded-xl shadow-sm">
          <div className="p-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <img
                src="/GA4logoicon.svg"
                alt="Google Analytics"
                className="w-5 h-5 flex-shrink-0"
              />
              <h2 className="font-semibold text-gray-800">Google Analytics – Source / Medium</h2>
            </div>
            <div className="flex items-center gap-2">
              <select
                className="px-2 py-1 bg-gray-50 border rounded-lg text-xs text-gray-700"
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
              >
                <option value="all">All</option>
                <option value="web">Web</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
                <option value="app">Only App (iOS + Android)</option>
              </select>
              <input
                type="text"
                placeholder="Search source / medium..."
                className="px-3 py-2 bg-gray-50 border rounded-lg text-sm w-52"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 border-collapse">
              <thead>
                <tr className="bg-gray-50 text-left text-[11px] text-gray-500">
                  <th className="px-3 py-2 font-medium border-b border-gray-200">
                    SOURCE / MEDIUM
                  </th>
                  {gaDateColumns.map((date) => (
                    <th
                      key={date}
                      className="px-3 py-2 font-medium text-right whitespace-nowrap border-b border-l border-gray-200"
                    >
                      {date}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {/* Non-paid bucket: direct, organic (other goes under Paid) */}
                {(() => {
                  const nonPaidSet = new Set(['direct', 'organic']);
                  const nonPaidRows = filteredGaRows.filter((r) => nonPaidSet.has(r.label));
                  const paidRows = filteredGaRows.filter((r) => !nonPaidSet.has(r.label));

                  return (
                    <>
                      {nonPaidRows.length > 0 && (
                        <>
                          <tr
                            className="bg-gray-50 cursor-pointer"
                            onClick={() => setIsNonPaidOpen((prev) => !prev)}
                          >
                            <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                              <span>Non‑paid</span>
                              <span className="ml-1 text-[10px]">
                                {isNonPaidOpen ? '▼' : '►'}
                              </span>
                            </td>
                            {gaDateColumns.map((date) => {
                              const metric = selectedMetric;
                              if (metric === 'conversion') {
                                let totalSessions = 0;
                                let totalConversions = 0;
                                nonPaidRows.forEach((r) => {
                                  const per = r.perDate?.[date];
                                  if (!per) return;
                                  totalSessions += Number(per.sessions || 0);
                                  totalConversions += Number(per.conversions || 0);
                                });
                                const rate =
                                  totalSessions > 0 ? totalConversions / totalSessions : 0;
                                return (
                                  <td
                                    key={date}
                                    className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                  >
                                    {formatMetricAggregate(rate, metric)}
                                  </td>
                                );
                              }
                              let total = 0;
                              nonPaidRows.forEach((r) => {
                                const per = r.perDate?.[date];
                                if (!per) return;
                                if (metric === 'revenue') total += Number(per.revenue || 0);
                                else if (metric === 'sessions')
                                  total += Number(per.sessions || 0);
                                else if (metric === 'installs')
                                  total += Number(per.installs || 0);
                              });
                              return (
                                <td
                                  key={date}
                                  className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                >
                                  {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                                </td>
                              );
                            })}
                          </tr>
                          {isNonPaidOpen &&
                            nonPaidRows.map((row, idx) => (
                              <tr key={`nonpaid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-[12px] font-medium text-gray-900">
                                  {row.label}
                                </td>
                                {gaDateColumns.map((date) => {
                                  const metric = selectedMetric;
                                  const per = row.perDate?.[date];
                                  if (!per) {
                                    return (
                                      <td
                                        key={date}
                                        className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200"
                                      >
                                        -
                                      </td>
                                    );
                                  }
                                  let display;
                                  if (metric === 'revenue') {
                                    display = formatCurrency(per.revenue);
                                  } else if (metric === 'sessions') {
                                    display = formatCompactNumber(per.sessions);
                                  } else if (metric === 'installs') {
                                    display = formatCompactNumber(per.installs);
                                  } else if (metric === 'conversion') {
                                    const rate =
                                      per.sessions > 0 ? per.conversions / per.sessions : 0;
                                    display = formatMetricAggregate(rate, metric);
                                  } else {
                                    display = formatCurrency(per.revenue);
                                  }
                                  return (
                                    <td
                                      key={date}
                                      className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200"
                                    >
                                      {display}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                        </>
                      )}

                      {paidRows.length > 0 && (
                        <>
                          <tr
                            className="bg-gray-50 cursor-pointer"
                            onClick={() => setIsPaidOpen((prev) => !prev)}
                          >
                            <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                              <span>Paid</span>
                              <span className="ml-1 text-[10px]">
                                {isPaidOpen ? '▼' : '►'}
                              </span>
                            </td>
                            {gaDateColumns.map((date) => {
                              const metric = selectedMetric;
                              if (metric === 'conversion') {
                                let totalSessions = 0;
                                let totalConversions = 0;
                                paidRows.forEach((r) => {
                                  const per = r.perDate?.[date];
                                  if (!per) return;
                                  totalSessions += Number(per.sessions || 0);
                                  totalConversions += Number(per.conversions || 0);
                                });
                                const rate =
                                  totalSessions > 0 ? totalConversions / totalSessions : 0;
                                return (
                                  <td
                                    key={date}
                                    className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                  >
                                    {formatMetricAggregate(rate, metric)}
                                  </td>
                                );
                              }
                              let total = 0;
                              paidRows.forEach((r) => {
                                const per = r.perDate?.[date];
                                if (!per) return;
                                if (metric === 'revenue') total += Number(per.revenue || 0);
                                else if (metric === 'sessions')
                                  total += Number(per.sessions || 0);
                                else if (metric === 'installs')
                                  total += Number(per.installs || 0);
                              });
                              return (
                                <td
                                  key={date}
                                  className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                >
                                  {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                                </td>
                              );
                            })}
                          </tr>
                          {isPaidOpen &&
                            paidRows.map((row, idx) => {
                              const isGoogleCpc = row.label?.toLowerCase() === 'google / cpc';
                              const isFacebookCpc = row.label?.toLowerCase() === 'facebook / cpc';
                              return (
                              <tr key={`paid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-[12px] font-medium text-gray-900">
                                  {row.label}
                                </td>
                                {gaDateColumns.map((date) => {
                                  const metric = selectedMetric;
                                  const per = row.perDate?.[date];
                                  // Get Google Ads cost for this date if this is google / cpc row
                                  const googleCost = isGoogleCpc ? (dailyGoogleAdsData.find((d) => d.date === date)?.cost || 0) : 0;
                                  // Get Meta Ads cost for this date if this is facebook / cpc row
                                  const metaCost = isFacebookCpc ? (dailyMetaAdsData.find((d) => d.date === date)?.cost || 0) : 0;
                                  
                                  if (!per) {
                                    return (
                                      <td
                                        key={date}
                                        className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200"
                                      >
                                        <div>-</div>
                                        {isGoogleCpc && googleCost > 0 && (
                                          <div className="text-[10px] text-red-500 mt-0.5">
                                            Cost: ₺{Math.round(googleCost).toLocaleString('tr-TR')}
                                          </div>
                                        )}
                                        {isFacebookCpc && metaCost > 0 && (
                                          <div className="text-[10px] text-red-500 mt-0.5">
                                            Cost: ₺{Math.round(metaCost).toLocaleString('tr-TR')}
                                          </div>
                                        )}
                                      </td>
                                    );
                                  }
                                  let display;
                                  if (metric === 'revenue') {
                                    display = formatCurrency(per.revenue);
                                  } else if (metric === 'sessions') {
                                    display = formatCompactNumber(per.sessions);
                                  } else if (metric === 'installs') {
                                    display = formatCompactNumber(per.installs);
                                  } else if (metric === 'conversion') {
                                    const rate =
                                      per.sessions > 0 ? per.conversions / per.sessions : 0;
                                    display = formatMetricAggregate(rate, metric);
                                  } else {
                                    display = formatCurrency(per.revenue);
                                  }
                                  return (
                                    <td
                                      key={date}
                                      className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200"
                                    >
                                      <div>{display}</div>
                                      {isGoogleCpc && googleCost > 0 && (
                                        <div className="text-[10px] text-red-500 mt-0.5">
                                          Cost: ₺{Math.round(googleCost).toLocaleString('tr-TR')}
                                        </div>
                                      )}
                                      {isFacebookCpc && metaCost > 0 && (
                                        <div className="text-[10px] text-red-500 mt-0.5">
                                          Cost: ₺{Math.round(metaCost).toLocaleString('tr-TR')}
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );})}
                        </>
                      )}
                    </>
                  );
                })()}

                {/* GA grand total row */}
                {filteredGaRows.length > 0 && (
                  <tr className="bg-gray-50">
                    <td className="px-3 py-2 text-[12px] font-bold text-gray-900 uppercase border-t border-gray-300">
                      Total
                    </td>
                    {gaDateColumns.map((date) => {
                      const metric = selectedMetric;
                      if (metric === 'conversion') {
                        let totalSessions = 0;
                        let totalConversions = 0;
                        filteredGaRows.forEach((r) => {
                          const per = r.perDate?.[date];
                          if (!per) return;
                          totalSessions += Number(per.sessions || 0);
                          totalConversions += Number(per.conversions || 0);
                        });
                        const rate =
                          totalSessions > 0 ? totalConversions / totalSessions : 0;
                        return (
                          <td
                            key={date}
                            className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 uppercase border-t border-l border-gray-300"
                          >
                            {formatMetricAggregate(rate, metric)}
                          </td>
                        );
                      }
                      let total = 0;
                      filteredGaRows.forEach((r) => {
                        const per = r.perDate?.[date];
                        if (!per) return;
                        if (metric === 'revenue') total += Number(per.revenue || 0);
                        else if (metric === 'sessions') total += Number(per.sessions || 0);
                        else if (metric === 'installs') total += Number(per.installs || 0);
                      });
                      return (
                        <td
                          key={date}
                          className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 uppercase border-t border-l border-gray-300"
                        >
                          {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                        </td>
                      );
                    })}
                  </tr>
                )}

                {filteredGaRows.length === 0 && (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-sm text-gray-500"
                      colSpan={1 + gaDateColumns.length}
                    >
                      No GA data for selected range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="border border-gray-200 rounded-xl shadow-sm">
          <div className="p-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <img
                src="/adjustlogopng.png"
                alt="Adjust"
                className="w-5 h-5 flex-shrink-0"
              />
              <h2 className="font-semibold text-gray-800">Adjust – Channels</h2>
            </div>
            <input
              type="text"
              placeholder="Search channel..."
              className="px-3 py-2 bg-gray-50 border rounded-lg text-sm w-52"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 border-collapse">
              <thead>
                <tr className="bg-gray-50 text-left text-[11px] text-gray-500">
                  <th className="px-3 py-2 font-medium border-b border-gray-200">CHANNEL</th>
                  {adjustDateColumns.map((date) => (
                    <th
                      key={date}
                      className="px-3 py-2 font-medium text-right whitespace-nowrap border-b border-l border-gray-200"
                    >
                      {date}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {(() => {
                  const nonPaidSet = new Set(['organic', 'other']);
                  const paidWhitelist = new Set([
                    'tiktok for business',
                    'pinterest',
                    'app samurai',
                    'facebook',
                    'rtb_app',
                    'reklam action',
                    'adventure_app',
                    'apple',
                    'google ads'
                  ]);

                  const nonPaidRows = filteredAdjustRows.filter((r) =>
                    nonPaidSet.has((r.label || '').toLowerCase())
                  );
                  const paidRows = filteredAdjustRows.filter((r) =>
                    paidWhitelist.has((r.label || '').toLowerCase())
                  );

                  return (
                    <>
                      {nonPaidRows.length > 0 && (
                        <>
                          <tr
                            className="bg-gray-50 cursor-pointer"
                            onClick={() => setIsAdjNonPaidOpen((prev) => !prev)}
                          >
                            <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                              <span>Non‑paid</span>
                              <span className="ml-1 text-[10px]">
                                {isAdjNonPaidOpen ? '▼' : '►'}
                              </span>
                            </td>
                            {adjustDateColumns.map((date) => {
                              const metric = selectedMetric;
                              if (metric === 'conversion') {
                                let totalSessions = 0;
                                let totalConversions = 0;
                                nonPaidRows.forEach((r) => {
                                  const per = r.perDate?.[date];
                                  if (!per) return;
                                  totalSessions += Number(per.sessions || 0);
                                  totalConversions += Number(per.conversions || 0);
                                });
                                const rate =
                                  totalSessions > 0 ? totalConversions / totalSessions : 0;
                                return (
                                  <td
                                    key={date}
                                    className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                  >
                                    {formatMetricAggregate(rate, metric)}
                                  </td>
                                );
                              }
                              let total = 0;
                              nonPaidRows.forEach((r) => {
                                const per = r.perDate?.[date];
                                if (!per) return;
                                if (metric === 'revenue') total += Number(per.revenue || 0);
                                else if (metric === 'sessions')
                                  total += Number(per.sessions || 0);
                                else if (metric === 'installs')
                                  total += Number(per.installs || 0);
                              });
                              return (
                                <td
                                  key={date}
                                  className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                >
                                  {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                                </td>
                              );
                            })}
                          </tr>
                          {isAdjNonPaidOpen &&
                            nonPaidRows.map((row, idx) => (
                              <tr key={`adj-nonpaid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-[12px] font-medium text-gray-900">
                                  {row.label}
                                </td>
                                {adjustDateColumns.map((date) => {
                                  const metric = selectedMetric;
                                  const per = row.perDate?.[date];
                                  if (!per) {
                                    return (
                                      <td
                                        key={date}
                                        className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200"
                                      >
                                        -
                                      </td>
                                    );
                                  }
                                  let display;
                                  if (metric === 'revenue') {
                                    display = formatCurrency(per.revenue);
                                  } else if (metric === 'sessions') {
                                    display = formatCompactNumber(per.sessions);
                                  } else if (metric === 'installs') {
                                    display = formatCompactNumber(per.installs);
                                  } else if (metric === 'conversion') {
                                    const rate =
                                      per.sessions > 0 ? per.conversions / per.sessions : 0;
                                    display = formatMetricAggregate(rate, metric);
                                  } else {
                                    display = formatCurrency(per.revenue);
                                  }
                                  return (
                                    <td
                                      key={date}
                                      className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200"
                                    >
                                      {display}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                        </>
                      )}

                      {paidRows.length > 0 && (
                        <>
                          <tr
                            className="bg-gray-50 cursor-pointer"
                            onClick={() => setIsAdjPaidOpen((prev) => !prev)}
                          >
                            <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                              <span>Paid</span>
                              <span className="ml-1 text-[10px]">
                                {isAdjPaidOpen ? '▼' : '►'}
                              </span>
                            </td>
                            {adjustDateColumns.map((date) => {
                              const metric = selectedMetric;
                              if (metric === 'conversion') {
                                let totalSessions = 0;
                                let totalConversions = 0;
                                paidRows.forEach((r) => {
                                  const per = r.perDate?.[date];
                                  if (!per) return;
                                  totalSessions += Number(per.sessions || 0);
                                  totalConversions += Number(per.conversions || 0);
                                });
                                const rate =
                                  totalSessions > 0 ? totalConversions / totalSessions : 0;
                                return (
                                  <td
                                    key={date}
                                    className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                  >
                                    {formatMetricAggregate(rate, metric)}
                                  </td>
                                );
                              }
                              let total = 0;
                              paidRows.forEach((r) => {
                                const per = r.perDate?.[date];
                                if (!per) return;
                                if (metric === 'revenue') total += Number(per.revenue || 0);
                                else if (metric === 'sessions')
                                  total += Number(per.sessions || 0);
                                else if (metric === 'installs')
                                  total += Number(per.installs || 0);
                              });
                              return (
                                <td
                                  key={date}
                                  className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 uppercase border-t border-l border-gray-200"
                                >
                                  {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                                </td>
                              );
                            })}
                          </tr>
                          {isAdjPaidOpen &&
                            paidRows.map((row, idx) => (
                              <tr key={`adj-paid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-[12px] font-medium text-gray-900">
                                  {row.label}
                                </td>
                                {adjustDateColumns.map((date) => {
                                  const metric = selectedMetric;
                                  const per = row.perDate?.[date];
                                  if (!per) {
                                    return (
                                      <td
                                        key={date}
                                        className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200"
                                      >
                                        -
                                      </td>
                                    );
                                  }
                                  let display;
                                  if (metric === 'revenue') {
                                    display = formatCurrency(per.revenue);
                                  } else if (metric === 'sessions') {
                                    display = formatCompactNumber(per.sessions);
                                  } else if (metric === 'installs') {
                                    display = formatCompactNumber(per.installs);
                                  } else if (metric === 'conversion') {
                                    const rate =
                                      per.sessions > 0 ? per.conversions / per.sessions : 0;
                                    display = formatMetricAggregate(rate, metric);
                                  } else {
                                    display = formatCurrency(per.revenue);
                                  }
                                  return (
                                    <td
                                      key={date}
                                      className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200"
                                    >
                                      {display}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                        </>
                      )}
                    </>
                  );
                })()}

                {/* Adjust grand total row */}
                {filteredAdjustRows.length > 0 && (
                  <tr className="bg-gray-50">
                    <td className="px-3 py-2 text-[12px] font-bold text-gray-900 uppercase border-t border-gray-300">
                      Total
                    </td>
                    {adjustDateColumns.map((date) => {
                      const metric = selectedMetric;
                      if (metric === 'conversion') {
                        let totalSessions = 0;
                        let totalConversions = 0;
                        filteredAdjustRows.forEach((r) => {
                          const per = r.perDate?.[date];
                          if (!per) return;
                          totalSessions += Number(per.sessions || 0);
                          totalConversions += Number(per.conversions || 0);
                        });
                        const rate =
                          totalSessions > 0 ? totalConversions / totalSessions : 0;
                        return (
                          <td
                            key={date}
                            className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 uppercase border-t border-l border-gray-300"
                          >
                            {formatMetricAggregate(rate, metric)}
                          </td>
                        );
                      }
                      let total = 0;
                      filteredAdjustRows.forEach((r) => {
                        const per = r.perDate?.[date];
                        if (!per) return;
                        if (metric === 'revenue') total += Number(per.revenue || 0);
                        else if (metric === 'sessions') total += Number(per.sessions || 0);
                        else if (metric === 'installs') total += Number(per.installs || 0);
                      });
                      return (
                        <td
                          key={date}
                          className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 uppercase border-t border-l border-gray-300"
                        >
                          {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                        </td>
                      );
                    })}
                  </tr>
                )}

                {filteredAdjustRows.length === 0 && (
                  <tr>
                    <td
                      className="px-3 py-6 text-center text-sm text-gray-500"
                      colSpan={1 + adjustDateColumns.length}
                    >
                      No Adjust data for selected range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Test email sender for this combined table */}
      <div className="mt-6">
        <Card className="border border-gray-200 rounded-xl shadow-sm">
          <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="font-semibold text-gray-800 text-sm">
                Combined Traffic Sources – Test Email
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Bu sayfadaki GA + Adjust pivot tablosunun bir kopyasını test amaçlı e‑posta olarak
                göndermek için kullan.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <input
                type="email"
                placeholder="ornek@company.com"
                className="px-3 py-2 bg-gray-50 border rounded-lg text-sm w-64"
                value={reportEmail}
                onChange={(e) => setReportEmail(e.target.value)}
              />
              <button
                onClick={handleSendCombinedReportEmail}
                disabled={isSendingReport}
                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isSendingReport ? 'Gönderiliyor...' : 'Test Mail Gönder'}
              </button>
            </div>
          </div>
          {sendReportStatus && (
            <div
              className={`px-4 pb-4 text-xs ${
                sendReportStatus.type === 'success' ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {sendReportStatus.message}
            </div>
          )}
        </Card>
      </div>
      </>
      )}

      {/* Monthly Section */}
      {activeSection === 'monthly' && (
        <div className="space-y-6">
          {monthlyLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="loading-logo-container">
                <img src="/logoicon.png" alt="Loading" className="loading-logo w-12 h-12 animate-pulse" />
              </div>
            </div>
          ) : (
            <>
              {/* Monthly Combined Summary Table */}
              <Card className="border border-gray-200 rounded-xl shadow-sm">
                <div className="p-4 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <img src="/GA4logoicon.svg" alt="GA" className="w-4 h-4" />
                      <span className="text-gray-400">+</span>
                      <img src="/adjustlogopng.png" alt="Adjust" className="w-4 h-4" />
                    </div>
                    <h2 className="font-semibold text-gray-800">Monthly Combined Summary (GA + Adjust)</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      className="px-2 py-1 text-sm border rounded-lg bg-white text-gray-700"
                      value={selectedMonthlyMetric}
                      onChange={(e) => setSelectedMonthlyMetric(e.target.value)}
                    >
                      <option value="revenue">Revenue</option>
                      <option value="sessions">Sessions</option>
                      <option value="installs">Installs</option>
                      <option value="conversion">Conversion Rate</option>
                    </select>
                    <button
                      className="p-1.5 text-gray-500 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      onClick={fetchMonthlyData}
                      title="Refresh data"
                    >
                      <RefreshCw className={`w-4 h-4 ${monthlyLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 border-collapse">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 text-left text-[11px] text-gray-600">
                        <th className="px-3 py-2 font-semibold border-b border-gray-200">SOURCE</th>
                        {(() => {
                          const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                          return Array.from(allMonths).sort().map((month) => (
                            <th key={month} className="px-3 py-2 font-semibold text-right whitespace-nowrap border-b border-l border-gray-200">
                              {month}
                            </th>
                          ));
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {/* GA Total Row */}
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <img src="/GA4logoicon.svg" alt="GA" className="w-4 h-4" />
                            <span>Google Analytics</span>
                          </div>
                        </td>
                        {(() => {
                          const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                          return Array.from(allMonths).sort().map((month) => {
                            const metric = selectedMonthlyMetric;
                            let total = 0;
                            let totalSessions = 0;
                            let totalConversions = 0;
                            filteredGaMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (!per) return;
                              if (metric === 'revenue') total += Number(per.revenue || 0);
                              else if (metric === 'sessions') total += Number(per.sessions || 0);
                              else if (metric === 'installs') total += Number(per.installs || 0);
                              else if (metric === 'conversion') {
                                totalSessions += Number(per.sessions || 0);
                                totalConversions += Number(per.conversions || 0);
                              }
                            });
                            if (metric === 'conversion') {
                              const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                              return (
                                <td key={month} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                                  {totalSessions === 0 ? '-' : formatMetricAggregate(rate, metric)}
                                </td>
                              );
                            }
                            return (
                              <td key={month} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                                {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                              </td>
                            );
                          });
                        })()}
                      </tr>
                      {/* Adjust Total Row */}
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <img src="/adjustlogopng.png" alt="Adjust" className="w-4 h-4" />
                            <span>Adjust</span>
                          </div>
                        </td>
                        {(() => {
                          const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                          return Array.from(allMonths).sort().map((month) => {
                            const metric = selectedMonthlyMetric;
                            let total = 0;
                            let totalSessions = 0;
                            let totalConversions = 0;
                            filteredAdjustMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (!per) return;
                              if (metric === 'revenue') total += Number(per.revenue || 0);
                              else if (metric === 'sessions') total += Number(per.sessions || 0);
                              else if (metric === 'installs') total += Number(per.installs || 0);
                              else if (metric === 'conversion') {
                                totalSessions += Number(per.sessions || 0);
                                totalConversions += Number(per.conversions || 0);
                              }
                            });
                            if (metric === 'conversion') {
                              const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                              return (
                                <td key={month} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                                  {totalSessions === 0 ? '-' : formatMetricAggregate(rate, metric)}
                                </td>
                              );
                            }
                            return (
                              <td key={month} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                                {total === 0 ? '-' : formatMetricAggregate(total, metric)}
                              </td>
                            );
                          });
                        })()}
                      </tr>
                      {/* Google Ads Cost Row */}
                      {monthlyPerformanceData?.['Google Ads'] && (
                        <tr className="hover:bg-red-50/30">
                          <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                              <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
                              <span>Google Ads (Cost)</span>
                            </div>
                          </td>
                          {(() => {
                            const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                            const googleMonths = monthlyPerformanceData['Google Ads']?.months || [];
                            // Create a map - monthKey needs +1 month shift to match actual month
                            const googleCostByMonth = {};
                            googleMonths.forEach(m => {
                              if (m.monthKey) {
                                // Add +1 month to raw monthKey to get actual month
                                const [yy, mm] = m.monthKey.split('-').map(Number);
                                const actualDate = new Date(yy, mm, 1); // mm is already 0-indexed + 1 = actual month
                                const actualMonthKey = `${actualDate.getFullYear()}-${String(actualDate.getMonth() + 1).padStart(2, '0')}`;
                                googleCostByMonth[actualMonthKey] = m.cost || 0;
                              }
                            });
                            return Array.from(allMonths).sort().map((month) => {
                              const cost = googleCostByMonth[month] || 0;
                              return (
                                <td key={month} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                                  {cost === 0 ? '-' : formatCurrency(cost)}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                      )}
                      {/* Meta Ads Cost Row */}
                      {monthlyPerformanceData?.['Meta Ads'] && (
                        <tr className="hover:bg-blue-50/30">
                          <td className="px-3 py-2.5 text-[12px] font-medium text-gray-900 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                              <img src="/6033716.png" alt="Meta Ads" className="w-4 h-4" />
                              <span>Meta Ads (Cost)</span>
                            </div>
                          </td>
                          {(() => {
                            const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                            const metaMonths = monthlyPerformanceData['Meta Ads']?.months || [];
                            // Create a map - monthKey needs +1 month shift to match actual month
                            const metaCostByMonth = {};
                            metaMonths.forEach(m => {
                              if (m.monthKey) {
                                // Add +1 month to raw monthKey to get actual month
                                const [yy, mm] = m.monthKey.split('-').map(Number);
                                const actualDate = new Date(yy, mm, 1); // mm is already 0-indexed + 1 = actual month
                                const actualMonthKey = `${actualDate.getFullYear()}-${String(actualDate.getMonth() + 1).padStart(2, '0')}`;
                                metaCostByMonth[actualMonthKey] = m.cost || 0;
                              }
                            });
                            return Array.from(allMonths).sort().map((month) => {
                              const cost = metaCostByMonth[month] || 0;
                              return (
                                <td key={month} className="px-3 py-2.5 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">
                                  {cost === 0 ? '-' : formatCurrency(cost)}
                                </td>
                              );
                            });
                          })()}
                        </tr>
                      )}
                      {/* Total Revenue Row (GA + Adjust) */}
                      <tr className="bg-green-50/50 hover:bg-green-50">
                        <td className="px-3 py-2.5 text-[12px] font-semibold text-green-800 border-t-2 border-gray-300">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                              <span className="text-white text-[8px] font-bold">₺</span>
                            </div>
                            <span>Total Revenue</span>
                          </div>
                        </td>
                        {(() => {
                          const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                          return Array.from(allMonths).sort().map((month) => {
                            let gaRevenue = 0, adjRevenue = 0;
                            filteredGaMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (per) gaRevenue += Number(per.revenue || 0);
                            });
                            filteredAdjustMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (per) adjRevenue += Number(per.revenue || 0);
                            });
                            const totalRevenue = gaRevenue + adjRevenue;
                            return (
                              <td key={month} className="px-3 py-2.5 text-[12px] font-semibold text-right text-green-800 border-t-2 border-l border-gray-300">
                                {totalRevenue === 0 ? '-' : formatCurrency(totalRevenue)}
                              </td>
                            );
                          });
                        })()}
                      </tr>
                      {/* Total Cost Row (Google Ads + Meta Ads) */}
                      <tr className="bg-red-50/50 hover:bg-red-50">
                        <td className="px-3 py-2.5 text-[12px] font-semibold text-red-800 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                              <span className="text-white text-[8px] font-bold">−</span>
                            </div>
                            <span>Total Cost</span>
                          </div>
                        </td>
                        {(() => {
                          const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                          const googleMonths = monthlyPerformanceData?.['Google Ads']?.months || [];
                          const metaMonths = monthlyPerformanceData?.['Meta Ads']?.months || [];
                          
                          // Build cost maps with +1 month shift
                          const googleCostByMonth = {};
                          googleMonths.forEach(m => {
                            if (m.monthKey) {
                              const [yy, mm] = m.monthKey.split('-').map(Number);
                              const actualDate = new Date(yy, mm, 1);
                              const actualMonthKey = `${actualDate.getFullYear()}-${String(actualDate.getMonth() + 1).padStart(2, '0')}`;
                              googleCostByMonth[actualMonthKey] = m.cost || 0;
                            }
                          });
                          const metaCostByMonth = {};
                          metaMonths.forEach(m => {
                            if (m.monthKey) {
                              const [yy, mm] = m.monthKey.split('-').map(Number);
                              const actualDate = new Date(yy, mm, 1);
                              const actualMonthKey = `${actualDate.getFullYear()}-${String(actualDate.getMonth() + 1).padStart(2, '0')}`;
                              metaCostByMonth[actualMonthKey] = m.cost || 0;
                            }
                          });
                          
                          return Array.from(allMonths).sort().map((month) => {
                            const googleCost = googleCostByMonth[month] || 0;
                            const metaCost = metaCostByMonth[month] || 0;
                            const totalCost = googleCost + metaCost;
                            return (
                              <td key={month} className="px-3 py-2.5 text-[12px] font-semibold text-right text-red-800 border-t border-l border-gray-200">
                                {totalCost === 0 ? '-' : formatCurrency(totalCost)}
                              </td>
                            );
                          });
                        })()}
                      </tr>
                      {/* ROI Row */}
                      <tr className="bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100">
                        <td className="px-3 py-2.5 text-[12px] font-bold text-purple-800 border-t-2 border-gray-300">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full bg-purple-600 flex items-center justify-center">
                              <span className="text-white text-[8px] font-bold">×</span>
                            </div>
                            <span>ROI</span>
                          </div>
                        </td>
                        {(() => {
                          const allMonths = new Set([...gaMonthlyColumns, ...adjustMonthlyColumns]);
                          const googleMonths = monthlyPerformanceData?.['Google Ads']?.months || [];
                          const metaMonths = monthlyPerformanceData?.['Meta Ads']?.months || [];
                          
                          // Build cost maps with +1 month shift
                          const googleCostByMonth = {};
                          googleMonths.forEach(m => {
                            if (m.monthKey) {
                              const [yy, mm] = m.monthKey.split('-').map(Number);
                              const actualDate = new Date(yy, mm, 1);
                              const actualMonthKey = `${actualDate.getFullYear()}-${String(actualDate.getMonth() + 1).padStart(2, '0')}`;
                              googleCostByMonth[actualMonthKey] = m.cost || 0;
                            }
                          });
                          const metaCostByMonth = {};
                          metaMonths.forEach(m => {
                            if (m.monthKey) {
                              const [yy, mm] = m.monthKey.split('-').map(Number);
                              const actualDate = new Date(yy, mm, 1);
                              const actualMonthKey = `${actualDate.getFullYear()}-${String(actualDate.getMonth() + 1).padStart(2, '0')}`;
                              metaCostByMonth[actualMonthKey] = m.cost || 0;
                            }
                          });
                          
                          return Array.from(allMonths).sort().map((month) => {
                            // Calculate Total Revenue
                            let gaRevenue = 0, adjRevenue = 0;
                            filteredGaMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (per) gaRevenue += Number(per.revenue || 0);
                            });
                            filteredAdjustMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (per) adjRevenue += Number(per.revenue || 0);
                            });
                            const totalRevenue = gaRevenue + adjRevenue;
                            
                            // Calculate Total Cost
                            const googleCost = googleCostByMonth[month] || 0;
                            const metaCost = metaCostByMonth[month] || 0;
                            const totalCost = googleCost + metaCost;
                            
                            // Calculate ROI as multiplier = Revenue / Cost
                            const roi = totalCost > 0 ? totalRevenue / totalCost : 0;
                            
                            return (
                              <td key={month} className="px-3 py-2.5 text-[12px] font-bold text-right text-purple-700 border-t-2 border-l border-gray-300">
                                {totalCost === 0 ? '-' : `${roi.toFixed(2)}x`}
                              </td>
                            );
                          });
                        })()}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Monthly GA Table */}
              <Card className="border border-gray-200 rounded-xl shadow-sm">
                <div className="p-4 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <img src="/GA4logoicon.svg" alt="Google Analytics" className="w-5 h-5 flex-shrink-0" />
                    <h2 className="font-semibold text-gray-800">Google Analytics – Source / Medium (Monthly)</h2>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-left text-[11px] text-gray-500">
                        <th className="px-3 py-2 font-medium border-b border-gray-200">SOURCE / MEDIUM</th>
                        {gaMonthlyColumns.map((month) => (
                          <th key={month} className="px-3 py-2 font-medium text-right whitespace-nowrap border-b border-l border-gray-200">
                            {month}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(() => {
                        const nonPaidSet = new Set(['direct', 'organic']);
                        const nonPaidRows = filteredGaMonthlyRows.filter((r) => nonPaidSet.has(r.label));
                        const paidRows = filteredGaMonthlyRows.filter((r) => !nonPaidSet.has(r.label));

                        return (
                          <>
                            {nonPaidRows.length > 0 && (
                              <>
                                <tr className="bg-gray-50 cursor-pointer" onClick={() => setIsMonthlyNonPaidOpen((prev) => !prev)}>
                                  <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                                    <span>Non‑paid</span>
                                    <span className="ml-1 text-[10px]">{isMonthlyNonPaidOpen ? '▼' : '►'}</span>
                                  </td>
                                  {gaMonthlyColumns.map((month) => {
                                    const metric = selectedMonthlyMetric;
                                    let total = 0, totalSessions = 0, totalConversions = 0;
                                    nonPaidRows.forEach((r) => {
                                      const per = r.perMonth?.[month];
                                      if (!per) return;
                                      if (metric === 'revenue') total += Number(per.revenue || 0);
                                      else if (metric === 'sessions') total += Number(per.sessions || 0);
                                      else if (metric === 'installs') total += Number(per.installs || 0);
                                      else { totalSessions += Number(per.sessions || 0); totalConversions += Number(per.conversions || 0); }
                                    });
                                    if (metric === 'conversion') {
                                      const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                                      return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{formatMetricAggregate(rate, metric)}</td>;
                                    }
                                    return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{total === 0 ? '-' : formatMetricAggregate(total, metric)}</td>;
                                  })}
                                </tr>
                                {isMonthlyNonPaidOpen && nonPaidRows.map((row, idx) => (
                                  <tr key={`m-nonpaid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-[12px] font-medium text-gray-900">{row.label}</td>
                                    {gaMonthlyColumns.map((month) => {
                                      const metric = selectedMonthlyMetric;
                                      const per = row.perMonth?.[month];
                                      if (!per) return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200">-</td>;
                                      let display;
                                      if (metric === 'revenue') display = formatCurrency(per.revenue);
                                      else if (metric === 'sessions') display = formatCompactNumber(per.sessions);
                                      else if (metric === 'installs') display = formatCompactNumber(per.installs);
                                      else { const rate = per.sessions > 0 ? per.conversions / per.sessions : 0; display = formatMetricAggregate(rate, metric); }
                                      return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">{display}</td>;
                                    })}
                                  </tr>
                                ))}
                              </>
                            )}
                            {paidRows.length > 0 && (
                              <>
                                <tr className="bg-gray-50 cursor-pointer" onClick={() => setIsMonthlyPaidOpen((prev) => !prev)}>
                                  <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                                    <span>Paid</span>
                                    <span className="ml-1 text-[10px]">{isMonthlyPaidOpen ? '▼' : '►'}</span>
                                  </td>
                                  {gaMonthlyColumns.map((month) => {
                                    const metric = selectedMonthlyMetric;
                                    let total = 0, totalSessions = 0, totalConversions = 0;
                                    paidRows.forEach((r) => {
                                      const per = r.perMonth?.[month];
                                      if (!per) return;
                                      if (metric === 'revenue') total += Number(per.revenue || 0);
                                      else if (metric === 'sessions') total += Number(per.sessions || 0);
                                      else if (metric === 'installs') total += Number(per.installs || 0);
                                      else { totalSessions += Number(per.sessions || 0); totalConversions += Number(per.conversions || 0); }
                                    });
                                    if (metric === 'conversion') {
                                      const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                                      return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{formatMetricAggregate(rate, metric)}</td>;
                                    }
                                    return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{total === 0 ? '-' : formatMetricAggregate(total, metric)}</td>;
                                  })}
                                </tr>
                                {isMonthlyPaidOpen && paidRows.map((row, idx) => (
                                  <tr key={`m-paid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-[12px] font-medium text-gray-900">{row.label}</td>
                                    {gaMonthlyColumns.map((month) => {
                                      const metric = selectedMonthlyMetric;
                                      const per = row.perMonth?.[month];
                                      if (!per) return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200">-</td>;
                                      let display;
                                      if (metric === 'revenue') display = formatCurrency(per.revenue);
                                      else if (metric === 'sessions') display = formatCompactNumber(per.sessions);
                                      else if (metric === 'installs') display = formatCompactNumber(per.installs);
                                      else { const rate = per.sessions > 0 ? per.conversions / per.sessions : 0; display = formatMetricAggregate(rate, metric); }
                                      return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">{display}</td>;
                                    })}
                                  </tr>
                                ))}
                              </>
                            )}
                          </>
                        );
                      })()}
                      {/* GA Monthly Total */}
                      {filteredGaMonthlyRows.length > 0 && (
                        <tr className="bg-gray-50">
                          <td className="px-3 py-2 text-[12px] font-bold text-gray-900 uppercase border-t border-gray-300">Total</td>
                          {gaMonthlyColumns.map((month) => {
                            const metric = selectedMonthlyMetric;
                            let total = 0, totalSessions = 0, totalConversions = 0;
                            filteredGaMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (!per) return;
                              if (metric === 'revenue') total += Number(per.revenue || 0);
                              else if (metric === 'sessions') total += Number(per.sessions || 0);
                              else if (metric === 'installs') total += Number(per.installs || 0);
                              else { totalSessions += Number(per.sessions || 0); totalConversions += Number(per.conversions || 0); }
                            });
                            if (metric === 'conversion') {
                              const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                              return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 border-t border-l border-gray-300">{formatMetricAggregate(rate, metric)}</td>;
                            }
                            return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 border-t border-l border-gray-300">{total === 0 ? '-' : formatMetricAggregate(total, metric)}</td>;
                          })}
                        </tr>
                      )}
                      {filteredGaMonthlyRows.length === 0 && (
                        <tr><td className="px-3 py-6 text-center text-sm text-gray-500" colSpan={1 + gaMonthlyColumns.length}>No GA data available.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Monthly Adjust Table */}
              <Card className="border border-gray-200 rounded-xl shadow-sm">
                <div className="p-4 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <img src="/adjustlogopng.png" alt="Adjust" className="w-5 h-5 flex-shrink-0" />
                    <h2 className="font-semibold text-gray-800">Adjust – Channels (Monthly)</h2>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 border-collapse">
                    <thead>
                      <tr className="bg-gray-50 text-left text-[11px] text-gray-500">
                        <th className="px-3 py-2 font-medium border-b border-gray-200">CHANNEL</th>
                        {adjustMonthlyColumns.map((month) => (
                          <th key={month} className="px-3 py-2 font-medium text-right whitespace-nowrap border-b border-l border-gray-200">
                            {month}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(() => {
                        const nonPaidSet = new Set(['organic', 'other']);
                        const paidWhitelist = new Set(['tiktok for business', 'pinterest', 'app samurai', 'facebook', 'rtb_app', 'reklam action', 'adventure_app', 'apple', 'google ads']);
                        const nonPaidRows = filteredAdjustMonthlyRows.filter((r) => nonPaidSet.has((r.label || '').toLowerCase()));
                        const paidRows = filteredAdjustMonthlyRows.filter((r) => paidWhitelist.has((r.label || '').toLowerCase()));

                        return (
                          <>
                            {nonPaidRows.length > 0 && (
                              <>
                                <tr className="bg-gray-50 cursor-pointer" onClick={() => setIsMonthlyAdjNonPaidOpen((prev) => !prev)}>
                                  <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                                    <span>Non‑paid</span>
                                    <span className="ml-1 text-[10px]">{isMonthlyAdjNonPaidOpen ? '▼' : '►'}</span>
                                  </td>
                                  {adjustMonthlyColumns.map((month) => {
                                    const metric = selectedMonthlyMetric;
                                    let total = 0, totalSessions = 0, totalConversions = 0;
                                    nonPaidRows.forEach((r) => {
                                      const per = r.perMonth?.[month];
                                      if (!per) return;
                                      if (metric === 'revenue') total += Number(per.revenue || 0);
                                      else if (metric === 'sessions') total += Number(per.sessions || 0);
                                      else if (metric === 'installs') total += Number(per.installs || 0);
                                      else { totalSessions += Number(per.sessions || 0); totalConversions += Number(per.conversions || 0); }
                                    });
                                    if (metric === 'conversion') {
                                      const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                                      return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{formatMetricAggregate(rate, metric)}</td>;
                                    }
                                    return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{total === 0 ? '-' : formatMetricAggregate(total, metric)}</td>;
                                  })}
                                </tr>
                                {isMonthlyAdjNonPaidOpen && nonPaidRows.map((row, idx) => (
                                  <tr key={`m-adj-nonpaid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-[12px] font-medium text-gray-900">{row.label}</td>
                                    {adjustMonthlyColumns.map((month) => {
                                      const metric = selectedMonthlyMetric;
                                      const per = row.perMonth?.[month];
                                      if (!per) return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200">-</td>;
                                      let display;
                                      if (metric === 'revenue') display = formatCurrency(per.revenue);
                                      else if (metric === 'sessions') display = formatCompactNumber(per.sessions);
                                      else if (metric === 'installs') display = formatCompactNumber(per.installs);
                                      else { const rate = per.sessions > 0 ? per.conversions / per.sessions : 0; display = formatMetricAggregate(rate, metric); }
                                      return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">{display}</td>;
                                    })}
                                  </tr>
                                ))}
                              </>
                            )}
                            {paidRows.length > 0 && (
                              <>
                                <tr className="bg-gray-50 cursor-pointer" onClick={() => setIsMonthlyAdjPaidOpen((prev) => !prev)}>
                                  <td className="px-3 py-2 text-[12px] font-bold text-gray-700 uppercase border-t border-gray-200">
                                    <span>Paid</span>
                                    <span className="ml-1 text-[10px]">{isMonthlyAdjPaidOpen ? '▼' : '►'}</span>
                                  </td>
                                  {adjustMonthlyColumns.map((month) => {
                                    const metric = selectedMonthlyMetric;
                                    let total = 0, totalSessions = 0, totalConversions = 0;
                                    paidRows.forEach((r) => {
                                      const per = r.perMonth?.[month];
                                      if (!per) return;
                                      if (metric === 'revenue') total += Number(per.revenue || 0);
                                      else if (metric === 'sessions') total += Number(per.sessions || 0);
                                      else if (metric === 'installs') total += Number(per.installs || 0);
                                      else { totalSessions += Number(per.sessions || 0); totalConversions += Number(per.conversions || 0); }
                                    });
                                    if (metric === 'conversion') {
                                      const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                                      return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{formatMetricAggregate(rate, metric)}</td>;
                                    }
                                    return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-700 border-t border-l border-gray-200">{total === 0 ? '-' : formatMetricAggregate(total, metric)}</td>;
                                  })}
                                </tr>
                                {isMonthlyAdjPaidOpen && paidRows.map((row, idx) => (
                                  <tr key={`m-adj-paid-${row.label}-${idx}`} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-[12px] font-medium text-gray-900">{row.label}</td>
                                    {adjustMonthlyColumns.map((month) => {
                                      const metric = selectedMonthlyMetric;
                                      const per = row.perMonth?.[month];
                                      if (!per) return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-400 border-t border-l border-gray-200">-</td>;
                                      let display;
                                      if (metric === 'revenue') display = formatCurrency(per.revenue);
                                      else if (metric === 'sessions') display = formatCompactNumber(per.sessions);
                                      else if (metric === 'installs') display = formatCompactNumber(per.installs);
                                      else { const rate = per.sessions > 0 ? per.conversions / per.sessions : 0; display = formatMetricAggregate(rate, metric); }
                                      return <td key={month} className="px-3 py-2 text-[12px] text-right text-gray-900 border-t border-l border-gray-200">{display}</td>;
                                    })}
                                  </tr>
                                ))}
                              </>
                            )}
                          </>
                        );
                      })()}
                      {/* Adjust Monthly Total */}
                      {filteredAdjustMonthlyRows.length > 0 && (
                        <tr className="bg-gray-50">
                          <td className="px-3 py-2 text-[12px] font-bold text-gray-900 uppercase border-t border-gray-300">Total</td>
                          {adjustMonthlyColumns.map((month) => {
                            const metric = selectedMonthlyMetric;
                            let total = 0, totalSessions = 0, totalConversions = 0;
                            filteredAdjustMonthlyRows.forEach((r) => {
                              const per = r.perMonth?.[month];
                              if (!per) return;
                              if (metric === 'revenue') total += Number(per.revenue || 0);
                              else if (metric === 'sessions') total += Number(per.sessions || 0);
                              else if (metric === 'installs') total += Number(per.installs || 0);
                              else { totalSessions += Number(per.sessions || 0); totalConversions += Number(per.conversions || 0); }
                            });
                            if (metric === 'conversion') {
                              const rate = totalSessions > 0 ? totalConversions / totalSessions : 0;
                              return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 border-t border-l border-gray-300">{formatMetricAggregate(rate, metric)}</td>;
                            }
                            return <td key={month} className="px-3 py-2 text-[11px] font-semibold text-right text-gray-900 border-t border-l border-gray-300">{total === 0 ? '-' : formatMetricAggregate(total, metric)}</td>;
                          })}
                        </tr>
                      )}
                      {filteredAdjustMonthlyRows.length === 0 && (
                        <tr><td className="px-3 py-6 text-center text-sm text-gray-500" colSpan={1 + adjustMonthlyColumns.length}>No Adjust data available.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {isCustomDateOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-96 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Select Custom Date Range
              </h3>
              <button
                onClick={() => setIsCustomDateOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Range
                </label>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <DatePicker
                    selectsRange
                    startDate={customStartDate}
                    endDate={customEndDate}
                    onChange={(dates) => {
                      const [start, end] = dates || [];
                      setCustomStartDate(start);
                      setCustomEndDate(end);
                    }}
                    inline
                    maxDate={new Date()}
                  />
                </div>
              </div>

              {customStartDate && customEndDate && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    Selected range:{' '}
                    <span className="font-medium">
                      {customStartDate.toLocaleDateString('tr-TR')} -{' '}
                      {customEndDate.toLocaleDateString('tr-TR')}
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCustomDateReset}
                className="flex-1 px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleCustomDateApply}
                disabled={!customStartDate || !customEndDate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


